import i18n from '@dinosband/bv-i18n'
import { PubSub } from '@dinosband/dbi-utils'

import {
  SmartQuery,
  SmartSubscribe,
  BvApolloClient,
} from '../apollo-client'

export * from './utils'
export { types } from '../types'

import { httpServer } from '../http-server'

import * as policy from '@dinosband/bv-main-schema/dist/policy'
import type * as types from '@dinosband/bv-main-schema/dist/types'
import {
  NewMessageDisplaySetting,
  ChatRoomType,
  MesgFilterType,
} from '@dinosband/bv-main-schema/dist/types'
import type {
  CreateJobReviewArgs,
  MesgSummaryCallback,
  ReadProfile_ChannelsOfChannelArgs,
  CreatePrivateChatRoomArgs,
  ChatRoomViewCallback,
} from '../apollo-client'
import type { UpdateJobOutputPublishedArgs } from './jobOutput'
import type { ReadJobOfferResponseArgs, ReadMyResponsesArgs } from './jobOffer'
import { convertSysNotiToString } from '@dinosband/bv-main-schema/dist/notification-message'
import { objCache } from '../utils/data-object-cache'
import { debug, DeferredUpdater, IntervalUpdater } from '@dinosband/dbi-utils'
import type { ChannelAndProfiles, ISysNotification, IMesgNotification } from '../types'
import type { CustomerRelationshipPartialCreateInput } from './crm'
import CryptoJS from 'crypto-js'
import { PaypalCreateOrderRequest } from './payment'
import { InAppPurchaseVerifyParams } from '@dinosband/payment-schema/src/index-client'

type CurrentChannelInfo = {
  id: number
  channel: types.Channel | undefined
}

import Vue from 'vue'
import { dictionaryManager } from '../utils/dictionary'
import { region1 as r1, region2 as r2 } from '@dinosband/bv-address'
import { Attachment } from '@dinosband/bv-attachment'

interface ISettings {
  development: boolean
  skipIntro: boolean
  userId: number
  authToken: string
  signOnCount: number
  signOnBlock: number
  sysInfo: string
  userGraph: string
  userGraphVersion: number
  categories: string
  userUsage: string
  referrerId: number
  blockedChannelIds: string
}

interface IThumbnailCache {
  get: (key: number) => Promise<string | null>
  set: (key: number, value: string) => Promise<void>
  initialize: () => Promise<void>
  reset: () => Promise<void>
}

interface IMessageCache {
  reset: () => Promise<void>
}

const USER_GRAPH_VERSION = 2

export class Store {
  settings: ISettings
  readonly httpURI: string
  readonly wsURI: string
  thumbnailCache: IThumbnailCache | undefined = undefined
  messageCache: IMessageCache | undefined = undefined
  rootMessageCache: IMessageCache | undefined = undefined
  messageNotiCache: IMessageCache | undefined = undefined
  failedMessageCache: IMessageCache | undefined = undefined
  apollo: BvApolloClient
  errorCb!: (err: string) => void
  deferredUpdater: DeferredUpdater
  usageUpdater: IntervalUpdater
  region1Dictionary = r1.dictionary()
  region2Dictionary = {} as Record<string, Record<string, string>>

  constructor (settings: ISettings, httpURI: string, wsURI: string, restURI: string) {
    this.settings = settings
    this.httpURI = httpURI
    this.wsURI = wsURI
    httpServer.setUrl(restURI)
    this.apollo = new BvApolloClient()

    // make any modifications to objCache be deferred update to userGraph
    this.deferredUpdater = new DeferredUpdater(() => {
      this.commitUserGraph()
    }, 3000)
    if (process.env.NODE_ENV != 'test') {
      objCache.onUpdated(() => {
        this.deferredUpdater.touch()
      })
    }

    this.usageUpdater = new IntervalUpdater(() => {
      if (this.userUsage) {
        this.upsertUserUsage(this.userUsage)
        this.commitUserUsage(this.userUsage)
      }
    // }, 30 * 60 * 1000)
    // }, 60 * 1000)
    }, 10 * 1000)
  }

  setCaches (
    messageCache: IMessageCache,
    thumbnailCache: IThumbnailCache,
    rootMessageCache: IMessageCache,
    messageNotiCache: IMessageCache,
    failedMessageCache: IMessageCache,
  ) {
    this.messageCache = messageCache
    this.thumbnailCache = thumbnailCache
    this.rootMessageCache = rootMessageCache
    this.messageNotiCache = messageNotiCache
    this.failedMessageCache = failedMessageCache
  }

  async initialize (appChannelId: number, errorCb: (err: string) => void) {
    this.appChannelId = appChannelId
    this.errorCb = errorCb
    this.setDevelMode(this.settings.development || false)

    this.skipIntro = this.settings.skipIntro
    // this.skipIntro = false

    if (this.settings.sysInfo) {
      this.commitSysInfo(JSON.parse(this.settings.sysInfo))
    }

    if (this.settings.categories) {
      if (this.settings.categories[0] === '[') {
        // when settings are saved as CategoryKeyword
        const categories = JSON.parse(this.settings.categories) as types.CategoryKeyword[]
        this.categoryKeywords = categories.map((cat) => cat.keyword)
      } else {
        this.categoryKeywords = this.settings.categories.split(',')
      }
    }

    if (this.settings.blockedChannelIds) {
      this.blockedChannelIds = this.settings.blockedChannelIds.split(',').map((s) => parseInt(s, 10))
    }

    // authToken should be set before create apollo client
    if (this.settings.userId && this.settings.authToken) {
      this.commitUserId(this.settings.userId)
      this.commitAuthToken(this.settings.authToken)

      if (this.settings.userUsage) {
        const usage = JSON.parse(this.settings.userUsage) as types.UserUsageData
        this.commitUserUsage(usage)
      }
    }

    dictionaryManager.initialize()
    await this.initCaches()

    if (Object.keys(this.region2Dictionary).length == 0) {
      this.region2Dictionary = await this.getDictionaryofRegionSecond()
    }
  }

  async initializeNetwork () {
    this.apollo.onReconnected(async () => {
      debug.log('!!! Reconnected: ', this.userId)
      this.readUserGraph()
      this.updateNotifications()
      // this.setUser(this.userId, this.authToken)
      // if (this.currentChannelId) {
      //   this.enterChannel(this.currentChannelId, true)
      // }
    })

    this.apollo.onError((error) => {
      if (error === 'maintenance') {
        debug.error('ApolloError: maintenance')
        this.maintenance = true
      } else if (error === 'invalidToken') {
        debug.error('ApolloError: invalidToken')
        this.setUser(0, '')
      } else if (error.startsWith && error.startsWith('invalid-user')) {
        debug.error('ApolloError: ', error)
        this.errorCb(error)
        // this.setUser(0, '')
      } else {
        debug.error('ApolloError: ', error)
        this.errorCb(error)
      }
    })

    if (this.httpURI) {
      await this.apollo.createApolloClient(this.httpURI, this.wsURI)
    }
  }

  async readUserGraphFromSettings () {
    if (this.userId) {
      if (this.settings.userGraph) {
        const key = process.env.LOCAL_STORAGE_KEY || 'localkey'
        try {
          // const CryptoJS = (await import('crypto-js')).default
          if (this.settings.userGraphVersion === USER_GRAPH_VERSION) {
            const userGraph = CryptoJS.AES.decrypt(this.settings.userGraph, key).toString(CryptoJS.enc.Utf8)
            const tick = Date.now()
            const user = objCache.fromJSON(userGraph) as types.User
            if (user) {
              debug.log('get userGraph from settings: tick = ', Date.now() - tick)
              this.commitUser(user)
              this.userReady = true
              this.watch.publish(this.USER_CHANGED)
              debug.log('userReady(store): ', Date.now() / 1000)
            } else {
              debug.error('failed to decode userGraph')
            }
          }
        } catch (e) {
          debug.error('Exception while decoding userGraph: ', e)
        }
      }
    } else {
      // Even if user is not logged in, publish USER_CHANGED,
      // so, subscribers can do some initialization.
      this.userReady = true
      this.watch.publish(this.USER_CHANGED)
    }
  }

  protected async initCaches () {
    const tick = Date.now()
    await this.thumbnailCache?.initialize()
    debug.log('mainStore.initCaches: tick = ', Date.now() - tick)
  }

  async readInitialData () {
    if (this.httpURI) {
      try {
        await this.readSysInfo()
        if (this.user) {
          await this.updateNotifications()
        } else {
          // 로그인하지 않은 사용자인 경우에는 다른 데이터를 읽을 것이 없으므로
          // 추천 플랫폼이 최대한 빨리 보일 수 있도록 한다.
          if (!this.appChannelId) {
            this.updateSuggestedChannels()
          }
        }
      } catch (e) {
        debug.error(e)
      }
    }
  }

  async readAdditionalData () {
    if (this.httpURI) {
      try {
        await this.readUserExtraInfo()
      } catch (e) {
        debug.error(e)
      }
    }
  }

  //--------------------------------------------------------------------------------
  // state
  //--------------------------------------------------------------------------------

  appChannelId = 0
  maintenance = false
  skipIntro = false
  agreeMarketing = false
  userId = 0
  user: types.User | null = null
  userReady = false
  readingUserId = 0
  authToken = ''
  userPenaltyNotified = ''
  userUsage: types.UserUsageData = {
    channelVisits: [],
    chatRoomVisits: [],
  }
  dontShowAgains: types.DontShowAgain[] = []
  isDontShowAgainsReady = false
  sysInfo: types.SysInfo | undefined = undefined
  sysInfoExt: types.SysInfoExt = {}
  secrets: types.Secret[] | undefined = undefined

  blockedChannelIds: number[] = []

  channels: types.Channel[] = []
  privateChatRooms: types.ChatRoom[] = []
  isPrivateChatRoomsReady = false
  // chatRoomMenus: types.ChannelChatRoomMenu[] = []

  // currentChannel: types.Channel | undefined = undefined
  // currentChannelId = 0
  // visitingChannel: types.Channel | undefined = undefined
  currentChannelInfo: CurrentChannelInfo = { id: 0, channel: undefined }
  categoryKeywords: string[] = []

  isNotificationReady = false
  sysNotifications: ISysNotification[] = []
  mesgNotifications: IMesgNotification[] = []
  // blockedProfiles: types.Profile[] = []
  // blockingProfiles: types.Profile[] = []
  // likedMesgIds: number[] = []
  // bookmarkedMesgIds: number[] = []
  develMode = false

  unreadSysNoticeCount = 0
  unreadAdsCount = 0

  latestMesgIds: number[] = []
  suggestedMesgIds: number[] = []
  suggestedMesgIdsUpdatedAt = 0
  suggestedChannelsRead: types.Channel[] = []
  suggestedChannelsUpdatedAt = 0

  // myJobOffers: types.JobOffer[] = []
  // myJobOfferResponses: types.JobOfferResponse[] = []

  qUserGraph: SmartQuery | null = null
  qAllChannels: SmartQuery | null = null
  qCurrentChannel: SmartQuery | null = null
  sChannelAdded: SmartSubscribe | null = null
  sChannelUpdated: SmartSubscribe | null = null
  sProfile_ChannelAdded: SmartSubscribe | null = null
  sProfile_ChannelUpdated: SmartSubscribe | null = null
  sProfile_ChannelDeleted: SmartSubscribe | null = null
  sProfileUpdated: SmartSubscribe | null = null
  sUserUpdated: SmartSubscribe | null = null
  sUser_ProfileAdded: SmartSubscribe | null = null
  sChatRoomUpdated: SmartSubscribe | null = null
  sChatRoomAdded: SmartSubscribe | null = null
  sDontShowAgainUpdated: SmartSubscribe | null = null
  sProfileReviewUpdated: SmartSubscribe | null = null
  sSysNotificationUpdated: SmartSubscribe | null = null
  sMesgNotificationUpdated: SmartSubscribe | null = null
  sUserBlockingAdded: SmartSubscribe | null = null
  sUserBlockingDeleted: SmartSubscribe | null = null
  sExpertInfoUpdated: SmartSubscribe | null = null
  sJobOfferUpdated: SmartSubscribe | null = null
  sJobOfferOfResponseUpdated: SmartSubscribe | null = null
  sJobOfferResponseUpdated: SmartSubscribe | null = null
  sJobOfferResponseOfOfferUpdated: SmartSubscribe | null = null
  sJobReviewAdded: SmartSubscribe | null = null
  sSysNoticeAdded: SmartSubscribe | null = null
  sAdvertisementAdded: SmartSubscribe | null = null
  sMessageSummaryUpdated: SmartSubscribe | null = null
  sChannelChatRoomMenuAdded: SmartSubscribe | null = null
  sChannelChatRoomMenuUpdated: SmartSubscribe | null = null
  sChannelChatRoomMenuDeleted: SmartSubscribe | null = null
  sCrmUpdated: SmartSubscribe | null = null

  //--------------------------------------------------------------------------------
  // watch
  //--------------------------------------------------------------------------------

  watch = new PubSub()
  USER_CHANGED = 'user'
  TOKEN_CHANGED = 'token'
  PROFILE_CHANGED = 'profile'
  CHANNEL_CHANGED = 'channel'
  USER_BLOCKING_CHANGED = 'user-blocking'
  JOB_OFFER_CHANGED = 'job-offer'
  JOB_OFFER_RESPONSE_CHANGED = 'job-offer-response'

  //--------------------------------------------------------------------------------
  // getters
  //--------------------------------------------------------------------------------

  get isLogin (): boolean {
    return !!this.user && this.userReady
  }

  get isReady (): boolean {
    return this.userReady
  }

  async getSecret (key: string): Promise<string | undefined> {
    const secrets = await this.readSecrets()
    const secret = secrets?.find((s) => s.key === key)
    return secret?.value
  }

  get profiles (): types.Profile[] {
    return this.user?.myProfiles ?? []
    // if (this.user?.profiles) {
    //   return this.user.profiles.map((u_p) => u_p.profile).sort((a, b) => a.id - b.id)
    // } else {
    //   return []
    // }
  }

  get profileIds (): number[] {
    return this.profiles.map((p) => p.id)
  }

  get myProfileChannels (): types.Profile_Channel[] {
    return this.user?.profileChannels ?? []
  }

  getMyProfileOfChannel (channelId: number): types.Profile | undefined {
    const pc = this.myProfileChannels.find((pc) => pc.channelId === channelId)
    if (pc) {
      return this.profiles.find((p) => p.id === pc.profileId)
    } else {
      return undefined
    }
  }

  get expelledChannelIds (): number[] {
    return this.myProfileChannels
      .filter((p_c) => p_c.expelledAt)
      .map((p_c) => p_c.channelId)
  }

  get joinedChannels (): ChannelAndProfiles[] {
    const joined = this.myProfileChannels
      .filter((p_c) => !p_c.expelledAt && p_c.channel)
      .map((p_c) => {
        const profile = this.user?.myProfiles.find((p) => p.id === p_c.profileId)
        return {
          channel: p_c.channel,
          profiles: profile ? [profile] : [],
          profile_channels: [p_c],
        }
      })
    return joined
  }

  get ownChannels (): ChannelAndProfiles[] {
    return this.joinedChannels
      .filter((cnp) => cnp.channel.ownerId === cnp.profiles[0].id)
  }

  get adminChannels (): types.Profile_Channel[] {
    return this.joinedChannels
      .map((cnp) => cnp.profile_channels[0])
      .filter((p_c) => p_c.isAdmin)
  }

  get joinedChannelIds (): number[] {
    return this.joinedChannels.map((cnp) => cnp.channel.id)
  }

  isJoinedChannelId (channelId: number): boolean {
    return this.joinedChannelIds.includes(channelId)
  }

  isJoined (channel: types.Channel): boolean {
    return this.isJoinedChannelId(channel.id)
  }

  getJoinedChannelAndProfileByChannelId (channelId: number): ChannelAndProfiles | undefined {
    return this.joinedChannels.find((cnp) => cnp.channel.id === channelId)
  }

  getJoinedChannelByChannelId (channelId: number): types.Channel | undefined {
    return this.joinedChannels.find((cnp) => cnp.channel.id === channelId)?.channel
  }

  getJoinedProfileChannelByChannelId (channelId: number): types.Profile_Channel | undefined {
    return this.joinedChannels.find((cnp) => cnp.channel.id === channelId)?.profile_channels?.[0]
  }

  get currentChannelId (): number {
    return this.currentChannelInfo.id
  }

  get currentChannel (): types.Channel | undefined {
    const info = this.currentChannelInfo
    if (info.id) {
      if (info.channel?.id === info.id) {
        return info.channel
      } else {
        return this.getJoinedChannelByChannelId(info.id)
      }
    }
    return undefined
  }

  get currentChannelProps (): types.ChannelProps {
    return JSON.parse(this.currentChannel?.props ?? '{}') as types.ChannelProps
  }

  get $currentChannel (): types.Channel {
    if (this.currentChannel) {
      return this.currentChannel
    }
    throw 'currentChannel is not set'
  }

  translate (target?: string | null, org?: string | null): string {
    if (!target) {
      return ''
    }
    return dictionaryManager.translate(this.currentChannelId, target, org) ?? org ?? target
  }

  translateSentence (target?: string | null): string {
    if (!target) {
      return ''
    }
    return dictionaryManager.translateSentence(this.currentChannelId, target)
  }

  async getDictionaryofRegionSecond (): Promise<Record<string, Record<string, string>>> {
    return await r2.dictionary()
  }

  regionText (region: string, level: number = 1): string {
    const dictionary = level == 1 ? this.region1Dictionary['en'] : this.region2Dictionary['en']
    return i18n.locale == 'ko' || !dictionary ? region : dictionary[region] ?? region
  }

  regionName (loc: string, checkDeep: boolean = false): string {
    const regions = loc.split(' ')
    const index = regions.length - 1
    let regionD = this.regionText(loc, index == 0 ? 1 : 2)
    if (checkDeep && i18n.locale != 'ko' && loc === regionD && index > 1) {
      regionD = this.regionCheck(`${regions[0]} ${regions[1]}`)
    }
    return regionD ?? loc
  }

  regionCheck (location: string): string {
    const regions = location.split(' ')
    const index = regions.length - 1
    const loc = index > 2 ? location.substring(0, location.lastIndexOf(' ') - 1) : location
    const regionD = this.regionText(loc, index == 0 ? 1 : 2)
    return regionD ?? loc
  }

  get currentChatRoomMenus (): types.ChannelChatRoomMenu[] {
    if (this.currentChannelInfo.channel) {
      return this.currentChannelInfo.channel.chatRoomMenus
    } else {
      console.error('currentChatRoomMenus: currentChannel is undefined')
      return []
      // return this.chatRoomMenus.filter((menu) => menu.channelId === this.currentChannelId)
    }
  }

  get channelIdsOfInterest (): number[] {
    const channelIds = [...this.joinedChannelIds]
    if (this.currentChannel && !channelIds.includes(this.currentChannelId)) {
      channelIds.push(this.currentChannelId)
    }
    return channelIds
  }

  get suggestedChannels (): types.Channel[] {
    const joinedIds = this.joinedChannelIds
    return this.suggestedChannelsRead.filter((ch) => !joinedIds.includes(ch.id) && !this.blockedChannelIds.includes(ch.id))
  }

  get currentChannelAndProfile (): ChannelAndProfiles | undefined {
    if (this.currentChannel) {
      return this.getJoinedChannelAndProfileByChannelId(this.currentChannelId)
    } else {
      return undefined
    }
  }

  get currentProfileChannel (): types.Profile_Channel | undefined {
    return this.myProfileChannels.find((pc) => pc.channelId === this.currentChannelId)
    // return this.currentChannelAndProfile?.profile_channels[0]
  }

  get currentProfile (): types.Profile | undefined {
    return this.currentChannelAndProfile?.profiles[0]
  }

  get defaultProfile (): types.Profile | undefined {
    if (this.profiles.length) {
      return this.profiles[0]
    }
    return undefined
    // throw 'no default profile'
  }

  // 운영자 또는 부운영자
  get amIAdmin (): boolean {
    if (this.currentChannel) {
      return this.amIAdminOfChannel(this.currentChannelId)
    } else {
      return false
    }
  }

  // 운영자
  get amIOwner (): boolean {
    if (this.currentChannel) {
      return this.amIOwnerOfChannel(this.currentChannelId)
    } else {
      return false
    }
  }

  amIAdminOfChannel (channelId: number): boolean {
    if (this.amIOwnerOfChannel(channelId)) {
      return true
    }
    const cnp = this.joinedChannels.find((cnp) => cnp.channel.id === channelId)
    if (cnp) {
      const p_c = cnp.profile_channels.find((p_c) => p_c.isAdmin)
      if (p_c) {
        return true
      }
    }
    return false
  }

  amIOwnerOfChannel (channelId: number): boolean {
    const cnp = this.joinedChannels.find((cnp) => cnp.channel.id === channelId)
    if (cnp) {
      if (this.profileIds.includes(cnp.channel.ownerId || 0)) {
        return true
      }
    }
    return false
  }

  getOwnerOfChannel (channel: types.Channel): types.Profile {
    return channel.owner
  }

  getChannelsOfProfile (profileId: number): Array<ChannelAndProfiles> {
    return this.joinedChannels.filter((ch) => ch.profiles.find((pf) => pf.id === profileId))
  }

  getNewMessageDisplaySetting (index: number): boolean | undefined {
    if (this.user?.newMessageDisplaySetting != undefined &&
      index >= NewMessageDisplaySetting.ALL_NEW_MESSAGES && index < NewMessageDisplaySetting.NAX) {
      return this.user.newMessageDisplaySetting & 1 << index ? true : false
    } else {
      return undefined
    }
  }

  isProfileBlocked (profile: types.Profile): boolean {
    const p = this.blockedProfileIds.find((id) => id === profile.id)
    return p ? true : false
  }

  isProfileIdBlocked (profileId: number): boolean {
    const p = this.blockedProfileIds.find((id) => id === profileId)
    return p ? true : false
  }

  showAlert (name: string): boolean {
    if (!this.isDontShowAgainsReady) {
      return false
    }
    if (this.dontShowAgains.find((ds) => ds.name === name)) {
      return false
    }
    return true
  }

  getPublicChatRoomIdsOfChannel (channelId: number): number[] {
    const channel = this.getJoinedChannelByChannelId(channelId)
    if (channel) {
      const chatRooms = this.publicChatRoomsOfChannel(channel)
      return chatRooms.map((c) => c.id)
    }
    return []
  }

  get myPhoneList (): string[] {
    const list: string[] = []
    if (this.user) {
      if (!this.user.tel.includes('@')) {
        list.push(this.user.tel.split(':')[0])
      }
      this.profiles.forEach((pf) => {
        if (pf.tel && !list.includes(pf.tel)) {
          list.push(pf.tel)
        }
      })
    }
    return list
  }

  get myEmailList (): string[] {
    const list: string[] = []
    if (this.user) {
      if (this.user.email) {
        list.push(this.user.email)
      }
      this.profiles.forEach((pf) => {
        if (pf.email && !list.includes(pf.email)) {
          list.push(pf.email)
        }
      })
    }
    return list
  }

  isPhoneVerified (tel: string): boolean {
    tel = tel.replace(/[^0-9]/g, '')
    if (this.user) {
      if (this.user.tel === tel) { return true }
      for (const profile of this.profiles) {
        if (profile.tel === tel && profile.telVerified) { return true }
      }
    }
    return false
  }

  isEmailVerified (email: string): boolean {
    if (this.user) {
      if (this.user.emailVerified && this.user.email === email) { return true }
      for (const profile of this.profiles) {
        if (profile.emailVerified && profile.email === email) {
          return true
        }
      }
    }
    return false
  }

  get userNotiSetting () {
    if (this.user?.notiSetting) {
      const notiSetting = JSON.parse(this.user.notiSetting) as types.UserNotiSetting
      return notiSetting
    } else {
      const notiSetting: types.UserNotiSetting = {
        enableAll: true,
        enableSys: true,
        enableMesg: true,
        enableMarketing: false,
        mesgDisabledChannelIds: [],
        mesgChatRooms: [],
      }
      return notiSetting
    }
  }

  get userBlockings (): types.UserBlocking[] {
    return this.user?.blockedUsers || []
  }

  get blockedProfileIds (): number[] {
    return this.user?.blockedUsers.map((blk) => blk.blockeeProfileId) ?? []
  }

  get blockingUserIds (): number[] {
    return this.user?.blockingUsers.map((blk) => blk.blockerId) ?? []
  }

  get publicChatRooms (): types.ChatRoom[] {
    return this.joinedChannels
      .flatMap((cnp) => cnp.channel.chatRoomMenus)
      .map((menu) => menu.chatRoom)
      .filter((menu) => !!menu) as types.ChatRoom[]
    // return this.chatRoomMenus
    //   .filter((m) => m.chatRoom)
    //   .map((m) => m.chatRoom as types.ChatRoom)
  }

  getChatRoomMenusOfChannel (channelId: number): types.ChannelChatRoomMenu[] {
    const channel = this.joinedChannels.find((cnp) => cnp.channel.id === channelId)?.channel
    return channel?.chatRoomMenus ?? []
    // return this.chatRoomMenus.filter((menu) => menu.channelId === channelId)
  }

  publicChatRoomsOfChannel (channel: types.Channel): types.ChatRoom[] {
    return this.publicChatRooms.filter((cr) => cr.channelId === channel.id)
  }

  get myChatRooms (): types.ChatRoom[] {
    return [...this.publicChatRooms, ...this.privateChatRooms]
  }

  participantUserIdsExceptedBlockingOfChatRoom (chatRoomId: number) {
    const chatRoom = this.privateChatRooms.find((cr) => cr.id === chatRoomId)
    if (chatRoom) {
      return chatRoom.participants
        .filter((p_c) => !this.blockingUserIds.includes(p_c.userId ?? 0))
        .map((p_c) => p_c.userId ?? 0)
    }
    return []
  }

  getChatRoomType (chatRoom: types.ChatRoom): types.ChatRoomType {
    if (chatRoom.type === ChatRoomType.JOB_OFFER) {
      const pstr = chatRoom.process
      if (pstr) {
        const process = JSON.parse(pstr) as types.ChatRoomProcess
        if (process.type === 'empty') {
          return ChatRoomType.INFO
        }
      }
      return chatRoom.type
    } else {
      return chatRoom.type
    }
  }

  parseMesgFilter (mesgFilter: string | undefined | null, channel?: types.Channel): types.MesgFilter {
    if (!channel) {
      channel = this.$currentChannel
    }
    const keywordSets = JSON.parse(channel.matchKeywords ?? '[]') as types.ChannelKeywordSet[]

    if (mesgFilter) {
      const filter = JSON.parse(mesgFilter) as types.MesgFilter
      if (filter.matchKeywords) {
        for (let i = 0; i < filter.matchKeywords.length; i++) {
          if (filter.matchKeywords[i][0] === '--') {
            filter.matchKeywords[i] = []
          }
        }
      } else {
        // old version
        filter.infoKeywords = []
        if (filter.keywords?.length) {
          filter.matchKeywords = [[...filter.keywords]]
        } else {
          filter.matchKeywords = [[]]
        }
      }
      if (filter.matchKeywords.length < keywordSets.length) {
        for (let i = filter.matchKeywords.length; i < keywordSets.length; i++) {
          filter.matchKeywords.push([])
        }
      }
      if (filter.locations?.length) {
        if (filter.locations[0] === '--') {
          filter.locations = []
        }
      }
      filter.version = 2
      filter.type = MesgFilterType.FILTER
      return filter
    } else {
      const filter: types.MesgFilter = {
        version: 2,
        type: MesgFilterType.FILTER,
        keywords: [],
        infoKeywords: [],
        matchKeywords: [],
        locations: [],
      }
      for (let i = 0; i < keywordSets.length; i++) {
        filter.matchKeywords.push([])
      }
      return filter
    }
  }

  getMatchKeywordSets (expertId?: number) {
    if (!expertId) {
      expertId = this.currentExpertId
    }
    const defaultSet: types.ChannelKeywordSet[] = [{ title: i18n.tc('components.channelsmenu120'), allowInput: true, keywords: [] }]
    const channel = this.$currentChannel

    if (channel.matchKeywords) {
      const sets = JSON.parse(channel.matchKeywords) as types.ChannelKeywordSet[]
      if (sets?.length) {
        const expertProps = this.getExpertPropsOfExpertId(expertId)
        expertProps.excludeMatchSets?.forEach((excl) => {
          if (excl < sets.length) {
            sets[excl].invalid = true
          }
        })
        expertProps.userInfosForm?.forEach((f) => {
          const ref = f as types.ChannelKeywordSetRef
          if (ref.isRef) {
            sets[ref.index].refTarget = ref.target
          }
        })
      }
      return sets || defaultSet
    } else {
      return defaultSet
    }
  }

  checkPermission (chatRoom: types.ChatRoom, accessType: policy.AccessType = 'enter'): boolean {

    const channel = this.$currentChannel
    const pc = this.currentProfileChannel

    return this.checkChatRoomPermissionOfChannel(channel, pc, chatRoom, accessType) === 'ok'
  }

  canAccessChatRoomOfChannel (channel: types.Channel, p_c: types.Profile_Channel | undefined, chatRoomId: number, accessType: policy.AccessType = 'enter'): boolean {
    return this.checkChatRoomPermissionOfChannel(channel, p_c, chatRoomId, accessType) === 'ok'
  }

  async canAccessChatRoom (channelId: number, chatRoomId: number, accessType: policy.AccessType = 'enter'): Promise<boolean> {
    return await this.checkChatRoomPermission(channelId, chatRoomId, accessType) === 'ok'
  }

  checkChatRoomPermissionOfChannel (channel: types.Channel, p_c: types.Profile_Channel | undefined, chatRoomId: number | types.ChatRoom, accessType: policy.AccessType = 'enter'): policy.PermissionResult {
    // const chatRoom = channel.chatRoomMenus.find((menu) => menu.chatRoomId === chatRoomId)?.chatRoom
    // const chatRoom = this.publicChatRooms.find((cr) => cr.id === chatRoomId)
    let chatRoom: types.ChatRoom | undefined | null
    if (typeof chatRoomId === 'number') {
      chatRoom = channel.chatRoomMenus.find((menu) => menu.chatRoom?.id === chatRoomId)?.chatRoom
    } else {
      chatRoom = chatRoomId as types.ChatRoom
    }
    if (!chatRoom) {
      return 'initialize'
    }


    const isJoined = !!p_c
    const props = JSON.parse(chatRoom.props ?? '{}') as types.ChatRoomProps
    if (!isJoined) {
      if (props.allowGuest && accessType === 'enter') {
        // 채팅방에 제한이 없는 경우에도 가입하지 않은 채널에서 읽기 이외의 기능은 제한한다.
        return 'ok'
      }
      return 'join'
    }

    if (chatRoom.restricted) {
      if (!p_c) {
        return 'join'
      }
      if (channel.ownerId === p_c.profileId) {
        return 'ok'
      }
      if (p_c.isAdmin) {
        return 'ok'
      }

      const canAccess = policy.checkChatRoomPermission({
        chatRoomId: chatRoom.id,
        status: chatRoom.status,
        restricted: chatRoom.restricted ?? '',
        profileId: p_c.profileId,
        isAdmin: false,
        channelExperts: channel.experts || [],
        expertInfos: this.myExpertInfosOfChannel(channel.id),
        passwordApprovedChatRoomIds: p_c.passwordApprovedChatRoomIds || '',
        participants: chatRoom.participants,
        accessType,
      })
      return canAccess
    } else {
      return 'ok'
    }
  }

  async checkChatRoomPermission (channelId: number, chatRoomId: number, accessType: policy.AccessType = 'enter'): Promise<policy.PermissionResult> {
    const cnp = this.getJoinedChannelAndProfileByChannelId(channelId)
    let channel: types.Channel | undefined
    if (cnp) {
      channel = cnp.channel
    } else {
      channel = await this.readChannelDetail(channelId)
    }
    if (!channel) {
      return 'initialize'
    }

    return this.checkChatRoomPermissionOfChannel(channel, cnp?.profile_channels[0], chatRoomId, accessType)
  }

  getTerm (chatRoomOrId: number | types.ChatRoom, key: string) {
    let chatRoom: types.ChatRoom | undefined
    if (typeof chatRoomOrId === 'number') {
      chatRoom = this.publicChatRooms.find((cr) => cr.id === chatRoomOrId)
      if (!chatRoom) {
        return ''
      }
    } else {
      chatRoom = chatRoomOrId
    }

    const chatRoomProps = JSON.parse(chatRoom.props || '{}') as types.ChatRoomProps
    const lang = 'ko'
    if (chatRoomProps.terms) {
      const langTerms = chatRoomProps.terms[lang]
      if (langTerms && langTerms[key]) {
        return langTerms[key]
      }
    }

    let channel: types.Channel | undefined
    if (chatRoom.channel) {
      channel = chatRoom.channel
    } else {
      channel = this.getJoinedChannelByChannelId(chatRoom.channelId || 0)
      if (!channel) {
        return ''
      }
    }

    const channelProps = JSON.parse(channel.props || '{}') as types.ChannelProps
    if (channelProps.terms) {
      const langTerms = channelProps.terms[lang]
      return langTerms[key] || ''
    }

    return ''
  }

  expertsOfChannel (channel: types.Channel): types.ChannelExpert[] {
    return channel.experts.filter((exp) => !exp.disabled)
  }

  defaultExpertOfChannel (channel: types.Channel): types.ChannelExpert | undefined {
    return channel.experts.find((exp) => !exp.disabled)
  }

  myExpertInfosOfChannel (channelId: number): types.ExpertInfo[] {
    const cnp = this.getJoinedChannelAndProfileByChannelId(channelId)
    const pc = cnp?.profile_channels[0]
    const channel = cnp?.channel
    if (!pc || !channel) {
      return []
    }
    if (this.amIAdminOfChannel(channelId)) {
      const expertIds = [0, ...channel.experts.map((ex) => ex.id)]
      return expertIds.map((expId) => ({
        id: 0,
        description: '',
        attachments: [],
        requestedAt: new Date(),
        approvedAt: new Date(),
        channelExpertId: expId,
        channelExpert: null as unknown as types.ChannelExpert,
        userId: this.userId,
        channelId,
        profile_ChannelId: pc.id,
        profile_Channel: pc,
        _count: {
          attachments: 0,
        },
      }))
    } else {
      const channelProps = JSON.parse(channel.props ?? '{}') as types.ChannelProps
      const infos = this.user?.expertInfos.filter((i) => i.channelId === channelId) ?? []
      if (!pc.noDefaultExpert && (!channelProps.noChangeExpert || !infos.length)) {
        infos.push({
          id: 0,
          description: '',
          attachments: [],
          requestedAt: new Date(),
          approvedAt: new Date(),
          channelExpertId: 0,
          channelExpert: null as unknown as types.ChannelExpert,
          userId: this.userId,
          channelId,
          profile_ChannelId: pc.id,
          profile_Channel: pc,
          _count: {
            attachments: 0,
          },
        })
      }
      return infos
    }
  }

  myExpertIdsOfChannel (channelId: number): number[] {
    const expInfos = this.myExpertInfosOfChannel(channelId)
    return expInfos.map((e) => e.channelExpertId)
    // if (expInfos.length) {
    //   return expInfos.map((e) => e.channelExpertId)
    // } else {
    //   return [0]
    // }
  }

  currentExpertIdOfChannel (channelId: number): number {
    const pc = this.getJoinedProfileChannelByChannelId(channelId)
    if (pc) {
      if (this.amIAdmin) {
        return pc.currentExpertId ?? 0
      } else {
        const expertIds = this.myExpertIdsOfChannel(channelId)
        if (pc.currentExpertId === undefined || pc.currentExpertId === null) {
          return expertIds[0]
        } else {
          if (expertIds.includes(pc.currentExpertId)) {
            return pc.currentExpertId
          } else {
            return expertIds[0]
          }
        }
      }
    } else {
      return 0
    }
  }

  get currentExpertId (): number {
    const channel = this.currentChannel
    if (channel) {
      return this.currentExpertIdOfChannel(channel.id)
    } else {
      return 0
    }
  }

  getExpertName (expertId: number): string {
    const nonExpertName = this.currentChannelProps.nonExpertName ?? i18n.tc('channel.nonExpert')
    if (expertId === 0) {
      return nonExpertName
    }
    const expert = this.expertsOfChannel(this.$currentChannel).find((e) => e.id === expertId)
    return expert?.name ?? nonExpertName
  }

  get currentExpertInfo (): types.ExpertInfo | undefined {
    const chExpId = this.currentExpertId
    return this.user?.expertInfos.find((i) => i.channelExpertId === chExpId)
  }

  get currentExpertProps (): types.ChannelExpertProps {
    return this.getExpertPropsOfExpertId(this.currentExpertId)
  }

  getExpertPropsOfExpertId (expId: number): types.ChannelExpertProps {
    const props: types.ChannelExpertProps = {}

    // legacy support
    const userInfosForm = this.currentChannelProps.inputFormAtJoinPerExpert?.[expId]
    if (userInfosForm) {
      props.userInfosForm = userInfosForm
    }
    const mesgFilters = this.currentChannelProps.filtersPerExpert?.[expId]
    if (mesgFilters) {
      props.mesgFilters = mesgFilters
    }

    // incremental apply
    const expertPropsList = this.currentChannelProps.expertProps ?? []
    expertPropsList.forEach((p) => {
      if (!p.expertIds || p.expertIds.includes(expId)) {
        if (p.merge) {
          // merge가 설정된 경우에 array는 merge하고 그 외의 값은 덮어쓴다.
          Object.entries(p).forEach(([key, value]) => {
            if (Array.isArray(value)) {
              const target = props as Record<string, unknown[]>
              if (target[key]) {
                target[key].push(...value)
              } else {
                target[key] = value
              }
            } else {
              const target = props as Record<string, unknown>
              target[key] = value
            }
          })
        } else {
          // overwrite
          Object.assign(props, p)
        }
      }
    })
    return props
  }

  getExpertIdsOfCurrentChannel (): number[] {
    return [0, ...this.$currentChannel.experts.map((e) => e.id)]
  }

  // matchExpertIds (channel: types.Channel, p_c: types.Profile_Channel, expertIds: number[], checkApproved: boolean, whenEmpty: boolean) {
  //   if (p_c.isAdmin || channel.ownerId === p_c.profileId) {
  //     return true
  //   }

  //   const myExpertInfos = checkApproved ?
  //     p_c.expertInfos
  //       ?.filter((i) => i.approvedAt && !i.deniedAt) ?? []
  //     : p_c.expertInfos ?? []

  //   const myExpertIds = myExpertInfos.map((i) => i.channelExpertId)

  //   if (expertIds.length) {
  //     if (myExpertIds.length === 0) {
  //       // non-expert user
  //       return expertIds.includes(0)
  //     } else {
  //       for (const id of myExpertIds) {
  //         if (expertIds.includes(id)) {
  //           return true
  //         }
  //       }
  //       return false
  //     }
  //   } else {
  //     return whenEmpty
  //   }
  // }

  // matchExpertIdsOfCurrentChannel (expertIds: number[], checkApproved: boolean, whenEmpty: boolean) {
  //   const cnp = this.currentChannelAndProfile
  //   if (cnp) {
  //     const channel = cnp.channel
  //     const p_c = cnp.profile_channels[0]
  //     return this.matchExpertIds(channel, p_c, expertIds, checkApproved, whenEmpty)
  //   }
  //   return false
  // }

  matchCurrentExpertOfChannel (channelId: number, expertIds: number[] | undefined, checkApproved: boolean, whenEmpty = true) {
    const expId = this.currentExpertIdOfChannel(channelId)
    if (expertIds?.length) {
      if (expertIds.includes(expId)) {
        if (checkApproved && expId) {
          const cnp = this.getJoinedChannelAndProfileByChannelId(channelId)
          const channel = cnp?.channel
          const pc = cnp?.profile_channels[0]
          if (pc?.isAdmin || channel?.ownerId === pc?.profileId) {
            return true
          }
          const expInfos = this.myExpertInfosOfChannel(channelId)
          const info = expInfos.find((i) => i.channelExpertId === expId)
          return Boolean(info?.approvedAt && !info?.deniedAt)
        } else {
          return true
        }
      } else {
        return false
      }
    } else {
      return whenEmpty
    }
  }

  matchCurrentExpert (expertIds: number[] | undefined, checkApproved: boolean, whenEmpty = true) {
    return this.matchCurrentExpertOfChannel(this.currentChannelId, expertIds, checkApproved, whenEmpty)
  }

  useCrm (expertId?: number) {
    const expertProps = this.getExpertPropsOfExpertId(expertId ?? this.currentExpertId)
    return expertProps.useCrm
  }

  //--------------------------------------------------------------------------------
  // actions
  //--------------------------------------------------------------------------------

  async requestSignInCode (tel: string) {
    const __user = await import('./user')
    return await __user.requestSignInCode(this, tel)
  }

  async checkUserExist (tel: string) {
    const __user = await import('./user')
    return await __user.checkUserExist(this, tel)
  }

  async checkEmailExist (email: string) {
    const __user = await import('./user')
    return await __user.checkEmailExist(this, email)
  }

  async requestCodeForChangingPhone (tel: string) {
    const __user = await import('./user')
    return await __user.requestCodeForChangingPhone(this, tel)
  }

  async requestSignInCodeViaEmail (tel: string) {
    const __user = await import('./user')
    return await __user.requestSignInCodeViaEmail(this, tel)
  }

  async signIn (input: types.SignInInput) {
    const __user = await import('./user')
    return await __user.signIn(this, input)
  }

  async signUp (input: types.SignUpInput) {
    const __user = await import('./user')
    return await __user.signUp(this, input)
  }

  async signOut () {
    const __user = await import('./user')
    return await __user.signOut(this)
  }

  async setUser (id: number, token: string) {
    const __user = await import('./user')
    return await __user.setUser(this, id, token)
  }

  async resetUser (userChanged = true) {
    const __user = await import('./user')
    return await __user.resetUser(this, userChanged)
  }

  async readUserGraph () {
    const __user = await import('./user')
    return await __user.readUserGraph(this)
  }

  // async findUsersByMesgFilter (input: types.FindUsersByMesgFilterInput) {
  //   const __user = await import('./user')
  //   return await __user.findUsersByMesgFilter(this, input)
  // }

  async readUserExtraInfo () {
    const __user = await import('./user')
    return await __user.readUserExtraInfo(this)
  }

  async startSubscriptions () {
    const __user = await import('./user')
    return await __user.startSubscriptions(this)
  }

  async checkReadingUserGraph () {
    const __user = await import('./user')
    return __user.checkReadingUserGraph(this)
  }

  async updateDeviceToken (deviceToken: string, deviceType: types.DeviceType, appVersion: string, lang?: string) {
    const __user = await import('./user')
    return await __user.updateDeviceToken(this, deviceToken, deviceType, appVersion, lang)
  }

  async updateUser (id: number, from: types.User, to: types.User) {
    const __user = await import('./user')
    return await __user.updateUser(this, id, from, to)
  }

  async deleteUser (reason: string) {
    const __user = await import('./user')
    return await __user.deleteUser(this, reason)
  }

  async resendEmail () {
    const __user = await import('./user')
    return await __user.resendEmail(this)
  }

  async agreeToMarketing (set = true) {
    const __user = await import('./user')
    return await __user.agreeToMarketing(this, set)
  }

  async updateUserNotiSetting (notiSetting: types.UserNotiSetting, agreeToMarketing: boolean | undefined = undefined) {
    const __user = await import('./user')
    return await __user.updateUserNotiSetting(this, notiSetting, agreeToMarketing)
  }

  async setDontShowAgain (name: string) {
    const __user = await import('./user')
    return await __user.setDontShowAgain(this, name)
  }

  async updateMyPhoneNumber (tel: string, code: string) {
    const __user = await import('./user')
    return await __user.updateMyPhoneNumber(this, tel, code)
  }

  async markSysNoticesRead (lastNoticeId: number) {
    const __user = await import('./user')
    return await __user.markSysNoticesRead(this, lastNoticeId)
  }

  async markAdvertisementsRead (lastId: number) {
    const __user = await import('./user')
    return await __user.markAdvertisementsRead(this, lastId)
  }

  async updateMesgFilter (p_c: types.Profile_Channel, mesgFilter: types.MesgFilter) {
    const __mesgFilter = await import('./mesgFilter')
    return await __mesgFilter.updateMesgFilter(this, p_c, mesgFilter)
  }

  async createProfile (input: types.ProfileCreateInput) {
    const __profile = await import('./profile')
    return await __profile.createProfile(this, input)
  }

  async createProfileWithoutUser (input: types.ProfileCreateInput) {
    const __profile = await import('./profile')
    return await __profile.createProfileWithoutUser(this, input)
  }

  async createProfilesWithoutUser (data: types.ProfileCreateInput[]) {
    const __profile = await import('./profile')
    return await __profile.createProfilesWithoutUser(this, data)
  }

  async checkProfileNameExist (name: string) {
    const __profile = await import('./profile')
    return await __profile.checkProfileNameExist(this, name)
  }

  async checkProfileExist (where: types.ProfileWhereInput) {
    const __profile = await import('./profile')
    return await __profile.checkProfileExist(this, where)
  }

  async checkProfileChannelExist (where: types.Profile_ChannelWhereInput) {
    const __profile = await import('./profile')
    return await __profile.checkProfileChannelExist(this, where)
  }

  async updateProfile (id: number, from: types.Profile, to: types.Profile) {
    const __profile = await import('./profile')
    return await __profile.updateProfile(this, id, from, to)
  }

  async deleteProfile (id: number, checkJoin = true) {
    const __profile = await import('./profile')
    return await __profile.deleteProfile(this, id, checkJoin)
  }

  async readProfile (id: number) {
    const __profile = await import('./profile')
    return await __profile.readProfile(this, id)
  }

  async readProfiles (ids: number[]) {
    const __profile = await import('./profile')
    return await __profile.readProfiles(this, ids)
  }

  async readProfileDetail (id: number) {
    const __profile = await import('./profile')
    return await __profile.readProfileDetail(this, id)
  }

  async readProfileUsers (id: number) {
    const __profile = await import('./profile')
    return await __profile.readProfileUsers(this, id)
  }

  async blockProfile (profileId: number, userId: number) {
    const __profile = await import('./profile')
    return await __profile.blockProfile(this, profileId, userId)
  }

  async unblockProfile (profileId: number, userId: number) {
    const __profile = await import('./profile')
    return await __profile.unblockProfile(this, profileId, userId)
  }

  async requestProfileEmailVerification (profileId: number) {
    const __profile = await import('./profile')
    return await __profile.requestProfileEmailVerification(this, profileId)
  }

  async requestProfileTelVerification (profileId: number) {
    const __profile = await import('./profile')
    return await __profile.requestProfileTelVerification(this, profileId)
  }

  async verifyProfileTelCode (profileId: number, code: string) {
    const __profile = await import('./profile')
    return await __profile.verifyProfileTelCode(this, profileId, code)
  }

  async setProfileBlockChatting (profileId: number, block: boolean) {
    const __profile = await import('./profile')
    return await __profile.setProfileBlockChatting(this, profileId, block)
  }

  async setAdmin (profile_ChannelId: number, isAdmin: boolean) {
    const __profile = await import('./profile')
    return await __profile.setAdmin(this, profile_ChannelId, isAdmin)
  }

  async updatePropsOfProfile_Channel (profile_ChannelId: number, props: types.Profile_ChannelProps) {
    const __profile = await import('./profile')
    return await __profile.updatePropsOfProfile_Channel(this, profile_ChannelId, props)
  }

  async updateUserInfoOfProfile_Channel (profile_ChannelId: number, userInfo: Record<string, unknown>, userAttachments?: Attachment[] | null) {
    const __profile = await import('./profile')
    return await __profile.updateUserInfoOfProfile_Channel(this, profile_ChannelId, userInfo, userAttachments)
  }

  async updateNamespaceOfProfile_Channel (profile_ChannelId: number, namespace: string) {
    const __profile = await import('./profile')
    return await __profile.updateNamespaceOfProfile_Channel(this, profile_ChannelId, namespace)
  }

  async updateAgreePrivacyAtOfProfile_Channel (profile_ChannelId: number, agreePrivacyAt: Date) {
    const __profile = await import('./profile')
    return await __profile.updateAgreePrivacyAtOfProfile_Channel(this, profile_ChannelId, agreePrivacyAt)
  }

  async updateCurrentExpertIdOfProfile_Channel (profile_ChannelId: number, currentExpertId: number) {
    const __profile = await import('./profile')
    return await __profile.updateCurrentExpertIdOfProfile_Channel(this, profile_ChannelId, currentExpertId)
  }

  async updateNoDefaultExpertOfProfile_Channel (profile_ChannelId: number, noDefault: boolean) {
    const __profile = await import('./profile')
    return await __profile.updateNoDefaultExpertOfProfile_Channel(this, profile_ChannelId, noDefault)
  }

  async updateFavoriteChatRoomsOfProfile_Channel (profile_ChannelId: number, chatRoomIds: (number | string)[]) {
    const __profile = await import('./profile')
    return await __profile.updateFavoriteChatRoomsOfProfile_Channel(this, profile_ChannelId, chatRoomIds)
  }

  async createExpertInfo (channelId: number, profile_channelId: number, channelExpertId: number, info: types.ExpertInfoInput) {
    const __expert = await import('./expert')
    return await __expert.createExpertInfo(this, channelId, profile_channelId, channelExpertId, info)
  }

  async readExpertInfo (id: number) {
    const __expert = await import('./expert')
    return await __expert.readExpertInfo(this, id)
  }

  async readExpertInfoOfProfileChannel (profile_ChannelId: number, channelExpertId: number) {
    const __expert = await import('./expert')
    return await __expert.readExpertInfoOfProfileChannel(this, profile_ChannelId, channelExpertId)
  }

  async readExpertInfosOfChannel (channelId: number) {
    const __expert = await import('./expert')
    return await __expert.readExpertInfosOfChannel(this, channelId)
  }

  async readChannelExperts (channelId: number) {
    const __expert = await import('./expert')
    return await __expert.readChannelExperts(this, channelId)
  }

  async searchExpertInfos (channelId: number, channelExpertId: number, userIds: number[]) {
    const __expert = await import('./expert')
    return await __expert.searchExpertInfos(this, channelId, channelExpertId, userIds)
  }

  async updateExpertInfo (from: types.ExpertInfo, to: types.ExpertInfo) {
    const __expert = await import('./expert')
    return await __expert.updateExpertInfo(this, from, to)
  }

  async deleteExpertInfo (expInfo: types.ExpertInfo) {
    const __expert = await import('./expert')
    return await __expert.deleteExpertInfo(this, expInfo)
  }

  async approveExpert (expInfoId: number) {
    const __expert = await import('./expert')
    return await __expert.approveExpert(this, expInfoId)
  }

  async denyExpert (expInfoId: number, reason: string | null) {
    const __expert = await import('./expert')
    return await __expert.denyExpert(this, expInfoId, reason)
  }

  async createprofileInfo (profileId: number, type: number, data: string) {
    const __profileInfo = await import('./profileInfo')
    return await __profileInfo.createprofileInfo(this, profileId, type, data)
  }

  async updateprofileInfo (id: number, type: number, data: string) {
    const __profileInfo = await import('./profileInfo')
    return await __profileInfo.updateprofileInfo(this, id, type, data)
  }

  async createJobReview (args: CreateJobReviewArgs) {
    const __jobReview = await import('./jobReview')
    return await __jobReview.createJobReview(this, args)
  }

  async readMyJobReviews (channelId?: number) {
    const __jobReview = await import('./jobReview')
    return await __jobReview.readMyJobReviews(this, channelId)
  }

  async readJobReviewsOfProfile (profileId: number) {
    const __jobReview = await import('./jobReview')
    return await __jobReview.readJobReviewsOfProfile(this, profileId)
  }

  async readJobReviewOfJobOfferResponse (jrsp: types.JobOfferResponse) {
    const __jobReview = await import('./jobReview')
    return await __jobReview.readJobReviewOfJobOfferResponse(this, jrsp)
  }

  async createJobOutput (data: types.JobOutputCreateInput) {
    const __jobOutput = await import('./jobOutput')
    return await __jobOutput.createJobOutput(this, data)
  }

  async searchJobOutput (filter: types.JobOutputSearchFilter, cursor?: number, take = 100) {
    const __jobOutput = await import('./jobOutput')
    return await __jobOutput.searchJobOutput(this, filter, cursor, take)
  }

  async updateJobOutputTitle (id: number, title: string) {
    const __jobOutput = await import('./jobOutput')
    return await __jobOutput.updateJobOutputTitle(this, id, title)
  }

  async updateJobOutputKeywords (id: number, keywords: string[]) {
    const __jobOutput = await import('./jobOutput')
    return await __jobOutput.updateJobOutputKeywords(this, id, keywords)
  }

  async updateJobOutputPublished (id: number, args: UpdateJobOutputPublishedArgs) {
    const __jobOutput = await import('./jobOutput')
    return await __jobOutput.updateJobOutputPublished(this, id, args)
  }

  async updateJobOutputProps (id: number, props: types.JobOfferProps) {
    const __jobOutput = await import('./jobOutput')
    return await __jobOutput.updateJobOutputProps(this, id, props)
  }

  async readChannel (id: number) {
    const __channel = await import('./channel')
    return await __channel.readChannel(this, id)
  }

  async readChannelDetail (id: number) {
    const __channel = await import('./channel')
    return await __channel.readChannelDetail(this, id)
  }

  async readUpdateMyChannelGraphs () {
    const __channel = await import('./channel')
    return await __channel.readUpdateMyChannelGraphs(this)
  }

  async searchChannels (keywords: string[]) {
    const __channel = await import('./channel')
    return await __channel.searchChannels(this, keywords)
  }

  async checkChannelNameExists (name: string) {
    const __channel = await import('./channel')
    return await __channel.checkChannelNameExists(this, name)
  }

  async createChannel (profileId: number, channel: types.Channel) {
    const __channel = await import('./channel')
    return await __channel.createChannel(this, profileId, channel)
  }

  async updateChannelData (id: number, data: types.ChannelUpdateInput) {
    const __channel = await import('./channel')
    return await __channel.updateChannelData(this, id, data)
  }

  async updateChannel (id: number, from: types.Channel, to: types.Channel) {
    const __channel = await import('./channel')
    return await __channel.updateChannel(this, id, from, to)
  }

  async enterChannel (channelId: number, force = false) {
    const __channel = await import('./channel')
    return await __channel.enterChannel(this, channelId, force)
  }

  async exitChannel () {
    const __channel = await import('./channel')
    return __channel.exitChannel(this)
  }

  async checkChannelAccessCode (channelId: number, code: string) {
    const __channel = await import('./channel')
    return await __channel.checkChannelAccessCode(this, channelId, code)
  }

  async joinChannel (input: types.JoinChannelInput) {
    const __channel = await import('./channel')
    return await __channel.joinChannel(this, input)
  }

  async joinChannelAndSetAll (input: types.JoinChannelAndSetAllInput) {
    const __channel = await import('./channel')
    return await __channel.joinChannelAndSetAll(this, input)
  }

  async leaveChannel (profileId: number, channelId: number) {
    const __channel = await import('./channel')
    return await __channel.leaveChannel(this, profileId, channelId)
  }

  async readProfile_ChannelsOfChannel (args: ReadProfile_ChannelsOfChannelArgs) {
    const __channel = await import('./channel')
    return await __channel.readProfile_ChannelsOfChannel(this, args)
  }

  async readProfile_ChannelsByIds (ids: number[]) {
    const __channel = await import('./channel')
    return await __channel.readProfile_ChannelsByIds(this, ids)
  }

  async readChannelsOfProfile (profileId: number) {
    const __channel = await import('./channel')
    return await __channel.readChannelsOfProfile(this, profileId)
  }

  async expelProfiles (channelId: number, profileIds: number[]) {
    const __channel = await import('./channel')
    return await __channel.expelProfiles(this, channelId, profileIds)
  }

  async unexpelProfiles (channelId: number, profileIds: number[]) {
    const __channel = await import('./channel')
    return await __channel.unexpelProfiles(this, channelId, profileIds)
  }

  async changeChannelOwner (id: number, profileId: number) {
    const __channel = await import('./channel')
    return await __channel.changeChannelOwner(this, id, profileId)
  }

  async updateSuggestedChannels () {
    const __channel = await import('./channel')
    return await __channel.updateSuggestedChannels(this)
  }

  async updateJoinedChannels () {
    const __channel = await import('./channel')
    return __channel.updateJoinedChannels(this)
  }

  async addMeOnWaitingOfChannel (channelId: number) {
    const __channel = await import('./channel')
    return await __channel.addMeOnWaitingOfChannel(this, channelId)
  }

  async readProfileChannelActivity (profielChannelId: number) {
    const __channel = await import('./channel')
    return await __channel.readProfileChannelActivity(this, profielChannelId)
  }

  async findProfileChannelActivityRanking (channelId: number, activity: types.Profile_ChannelActivity) {
    const __channel = await import('./channel')
    return await __channel.findProfileChannelActivityRanking(this, channelId, activity)
  }

  async findProfileChannelActivityTopFollowers (channelId: number, count: number) {
    const __channel = await import('./channel')
    return await __channel.findProfileChannelActivityTopFollowers(this, channelId, count)
  }

  async findProfileChannelActivityTopPoints (channelId: number, count: number) {
    const __channel = await import('./channel')
    return await __channel.findProfileChannelActivityTopPoints(this, channelId, count)
  }

  async findProfileChannelActivityTopPointsTerm (channelId: number, count: number) {
    const __channel = await import('./channel')
    return await __channel.findProfileChannelActivityTopPointsTerm(this, channelId, count)
  }

  async findChannelReferrer (channelId: number, tel: string, email: string) {
    const __channel = await import('./channel')
    return await __channel.findChannelReferrer(this, channelId, tel, email)
  }

  async createProduct (data: types.ProductCreateInput) {
    const __product = await import('./product')
    return await __product.createProduct(this, data)
  }

  async readProducts (channelId: number, category?: string, subCategory?: string) {
    const __product = await import('./product')
    return await __product.readProducts(this, channelId, category, subCategory)
  }

  async readProduct (id: number) {
    const __product = await import('./product')
    return await __product.readProduct(this, id)
  }

  async updateProduct (id: number, data: types.ProductUpdateInput) {
    const __product = await import('./product')
    return await __product.updateProduct(this, id, data)
  }

  async deleteProduct (id: number) {
    const __product = await import('./product')
    return await __product.deleteProduct(this, id)
  }

  async upsertChannelDictionary (channelId: number, lang: string, dict: Record<string, string>) {
    const __dictionary = await import('./dictionary')
    return await __dictionary.upsertChannelDictionary(this, channelId, lang, dict)
  }

  async readChannelDictionary (channelId: number, lang: string) {
    const __dictionary = await import('./dictionary')
    return await __dictionary.readChannelDictionary(this, channelId, lang)
  }

  async createCustomerRelationship (customerPcId: number, data: CustomerRelationshipPartialCreateInput): Promise<types.CustomerRelationship | undefined> {
    const __crm = await import('./crm')
    return await __crm.createCustomerRelationship(this, customerPcId, data)
  }

  async readCustomerRelationships (userPcId: number, customerPcIds?: number[], where?: types.CustomerRelationshipWhereInput) {
    const __crm = await import('./crm')
    return await __crm.readCustomerRelationships(this, userPcId, customerPcIds, where)
  }

  async updateCustomerRelationship (id: number, data: types.CustomerRelationshipUpdateInput) {
    const __crm = await import('./crm')
    return await __crm.updateCustomerRelationship(this, id, data)
  }

  async updateCrmStage (jrsp: types.JobOfferResponse, stage: number) {
    const __crm = await import('./crm')
    return await __crm.updateCrmStage(this, jrsp, stage)
  }

  async createCrmLog (input: types.CustomerCommunicationLogCreateInput) {
    const __crm = await import('./crm')
    return await __crm.createCrmLog(this, input)
  }

  async readCrmLogs (crmId: number) {
    const __crm = await import('./crm')
    return await __crm.readCrmLogs(this, crmId)
  }

  async readCategoryKeywords () {
    const __category = await import('./category')
    return await __category.readCategoryKeywords(this)
  }

  async createChatRoom (channelId: number, data: types.ChatRoomCreateWithoutChannelInput) {
    const __chatRoom = await import('./chatRoom')
    return await __chatRoom.createChatRoom(this, channelId, data)
  }

  async createPrivateChatRoom (args: CreatePrivateChatRoomArgs) {
    const __chatRoom = await import('./chatRoom')
    return await __chatRoom.createPrivateChatRoom(this, args)
  }

  async createGroupChatRoom (rootMessageId: number, channelId: number) {
    const __chatRoom = await import('./chatRoom')
    return await __chatRoom.createGroupChatRoom(this, rootMessageId, channelId)
  }

  async createCommentsChatRoom (commentsRootMessageId: number, commentsRootChatRoomId: number, channelId: number) {
    const __chatRoom = await import('./chatRoom')
    return await __chatRoom.createCommentsChatRoom(this, commentsRootMessageId, commentsRootChatRoomId, channelId)
  }

  async createHelpDeskChatRoom (channelId: number, profileId: number) {
    const __chatRoom = await import('./chatRoom')
    return await __chatRoom.createHelpDeskChatRoom(this, channelId, profileId)
  }

  async readChatRoomDetail (id: number) {
    const __chatRoom = await import('./chatRoom')
    return await __chatRoom.readChatRoomDetail(this, id)
  }

  async readChannelOfChatRoom (chatRoomId: number) {
    const __chatRoom = await import('./chatRoom')
    return await __chatRoom.readChannelOfChatRoom(this, chatRoomId)
  }

  async readChatRooms (chatRoomIds: number[]) {
    const __chatRoom = await import('./chatRoom')
    return await __chatRoom.readChatRooms(this, chatRoomIds)
  }

  async readPublicChatRoomsOfChannel (channelId: number) {
    const __chatRoom = await import('./chatRoom')
    return await __chatRoom.readPublicChatRoomsOfChannel(this, channelId)
  }

  async updateChatRoom (from: types.ChatRoom, to?: types.ChatRoom, update?: types.ChatRoomUpdateInput) {
    const __chatRoom = await import('./chatRoom')
    return await __chatRoom.updateChatRoom(this, from, to, update)
  }

  async updateChatRoomStatus (id: number, status: types.ChatRoomStatus) {
    const __chatRoom = await import('./chatRoom')
    return await __chatRoom.updateChatRoomStatus(this, id, status)
  }

  async getPermissionOfChatRoom (chatRoomId: number, profile_ChannelId: number, code: string) {
    const __chatRoom = await import('./chatRoom')
    return await __chatRoom.getPermissionOfChatRoom(this, chatRoomId, profile_ChannelId, code)
  }

  async readMyPublicChatRooms () {
    const __chatRoom = await import('./chatRoom')
    return await __chatRoom.readMyPublicChatRooms(this)
  }

  async readMyPrivateChatRooms () {
    const __chatRoom = await import('./chatRoom')
    return await __chatRoom.readMyPrivateChatRooms(this)
  }

  async readGroupChatRoom (rootMessageId: number, userId: number) {
    const __chatRoom = await import('./chatRoom')
    return await __chatRoom.readGroupChatRoom(this, rootMessageId, userId)
  }

  async readHelpDeskChatRooms () {
    const __chatRoom = await import('./chatRoom')
    return await __chatRoom.readHelpDeskChatRooms(this)
  }

  async readCommentsChatRoom (chatRoomId: number | undefined, commentsRootMessageId: number | undefined) {
    const __chatRoom = await import('./chatRoom')
    return await __chatRoom.readCommentsChatRoom(this, chatRoomId, commentsRootMessageId)
  }

  async readUser_ChatRoomViews (chatRoomIds?: number[], userId?: number) {
    const __chatRoomView = await import('./chatRoomView')
    return await __chatRoomView.readUser_ChatRoomViews(this, chatRoomIds, userId)
  }

  async updateUser_ChatRoomView (id: number, data: types.User_ChatRoomViewUpdateInput) {
    const __chatRoomView = await import('./chatRoomView')
    return await __chatRoomView.updateUser_ChatRoomView(this, id, data)
  }

  async upsertUser_ChatRoomView (chatRoomId: number) {
    const __chatRoomView = await import('./chatRoomView')
    return await __chatRoomView.upsertUser_ChatRoomView(this, chatRoomId)
  }

  async subscribeOnUser_ChatRoomViewUpdated (chatRoomId: number, userId: number, cb: ChatRoomViewCallback) {
    const __chatRoomView = await import('./chatRoomView')
    return await __chatRoomView.subscribeOnUser_ChatRoomViewUpdated(this, chatRoomId, userId, cb)
  }

  async createChatRoomMenu (channelId: number, chatRoomId: number, input: types.ChannelChatRoomMenuCreateWithoutChannelInput) {
    const __chatRoomMenu = await import('./chatRoomMenu')
    return await __chatRoomMenu.createChatRoomMenu(this, channelId, chatRoomId, input)
  }

  async updateChatRoomMenu (from: types.ChannelChatRoomMenu, to: types.ChannelChatRoomMenu) {
    const __chatRoomMenu = await import('./chatRoomMenu')
    return await __chatRoomMenu.updateChatRoomMenu(this, from, to)
  }

  async updateChatRoomMenuSeq (id: number, seq: number) {
    const __chatRoomMenu = await import('./chatRoomMenu')
    return await __chatRoomMenu.updateChatRoomMenuSeq(this, id, seq)
  }

  async deleteChatRoomMenu (id: number) {
    const __chatRoomMenu = await import('./chatRoomMenu')
    return await __chatRoomMenu.deleteChatRoomMenu(this, id)
  }

  async createSysNotification (receiverProfileId: number, type: types.SysNotificationType, info: string) {
    const __notification = await import('./notification')
    return await __notification.createSysNotification(this, receiverProfileId, type, info)
  }

  async deleteSysNotification (sysNotiId: number) {
    const __notification = await import('./notification')
    return await __notification.deleteSysNotification(this, sysNotiId)
  }

  async deleteAllSysNotifications () {
    const __notification = await import('./notification')
    return await __notification.deleteAllSysNotifications(this)
  }

  async setAllSysNotiRead () {
    const __notification = await import('./notification')
    return await __notification.setAllSysNotiRead(this)
  }

  async deleteMesgNotification (messageId: number) {
    const __notification = await import('./notification')
    return await __notification.deleteMesgNotification(this, messageId)
  }

  async deleteAllMesgNotifications () {
    const __notification = await import('./notification')
    return await __notification.deleteAllMesgNotifications(this)
  }

  async setAllMesgNotiRead () {
    const __notification = await import('./notification')
    return await __notification.setAllMesgNotiRead(this)
  }

  async readMesgNotifications (userId: number, cursor?: number, before?: boolean) {
    const __notification = await import('./notification')
    return await __notification.readMesgNotifications(this, userId, cursor, before)
  }

  async readSysNotifications (profileIds: number[], cursor?: number, before?: boolean) {
    const __notification = await import('./notification')
    return await __notification.readSysNotifications(this, profileIds, cursor, before)
  }

  async updateNotifications () {
    const __notification = await import('./notification')
    return await __notification.updateNotifications(this)
  }

  async reportAbusingProfile (profileId: number, channelId: number | undefined, description: string) {
    const __abusing = await import('./abusing')
    return await __abusing.reportAbusingProfile(this, profileId, channelId, description)
  }

  async reportAbusingChannel (channelId: number, description: string) {
    const __abusing = await import('./abusing')
    return await __abusing.reportAbusingChannel(this, channelId, description)
  }

  async reportAbusingMessage (chatRoomId: number, profileId: number, messageId: number, description: string) {
    const __abusing = await import('./abusing')
    return await __abusing.reportAbusingMessage(this, chatRoomId, profileId, messageId, description)
  }

  async readMyAbusingReports () {
    const __abusing = await import('./abusing')
    return await __abusing.readMyAbusingReports(this)
  }

  async readThumbnail (id: number) {
    const __thumbnail = await import('./thumbnail')
    return await __thumbnail.readThumbnail(this, id)
  }

  async createThumbnail (data: string) {
    const __thumbnail = await import('./thumbnail')
    return await __thumbnail.createThumbnail(this, data)
  }

  async readMyJobOffers () {
    const __jobOffer = await import('./jobOffer')
    return await __jobOffer.readMyJobOffers(this)
  }

  async createJobOffer (chatRoomId: number, profileId: number, mesgId?: number) {
    const __jobOffer = await import('./jobOffer')
    return await __jobOffer.createJobOffer(this, chatRoomId, profileId, mesgId)
  }

  async readJobOffer (id: number) {
    const __jobOffer = await import('./jobOffer')
    return await __jobOffer.readJobOffer(this, id)
  }

  async readJobOfferGraph (id: number) {
    const __jobOffer = await import('./jobOffer')
    return await __jobOffer.readJobOfferGraph(this, id)
  }

  async readJobOffers (variables: types.QueryFindManyJobOfferArgs, withClient = false) {
    const __jobOffer = await import('./jobOffer')
    return await __jobOffer.readJobOffers(this, variables, withClient)
  }

  async readJobOffersByIds (ids: number[]) {
    const __jobOffer = await import('./jobOffer')
    return await __jobOffer.readJobOffersByIds(this, ids)
  }

  async readJobOffersOfProfile (profileId: number, channelId: number) {
    const __jobOffer = await import('./jobOffer')
    return await __jobOffer.readJobOffersOfProfile(this, profileId, channelId)
  }

  async readMyJobOffersOfChannel (channelId: number, cursor?: number, take = 10) {
    const __jobOffer = await import('./jobOffer')
    return await __jobOffer.readMyJobOffersOfChannel(this, channelId, cursor, take)
  }

  async readMyJobOffersOfChatRooms (chatRoomIds: number[], openOnly: boolean, cursor?: number, take = 10) {
    const __jobOffer = await import('./jobOffer')
    return await __jobOffer.readMyJobOffersOfChatRooms(this, chatRoomIds, openOnly, cursor, take)
  }

  async readJobOfferResponse (args: ReadJobOfferResponseArgs) {
    const __jobOffer = await import('./jobOffer')
    return await __jobOffer.readJobOfferResponse(this, args)
  }

  async readJobOfferResponses (args: types.QueryFindManyJobOfferResponseArgs, withProfiles = false) {
    const __jobOffer = await import('./jobOffer')
    return await __jobOffer.readJobOfferResponses(this, args, withProfiles)
  }

  async readJobOfferResponsesOfJobOffer (jobOfferId: number) {
    const __jobOffer = await import('./jobOffer')
    return await __jobOffer.readJobOfferResponsesOfJobOffer(this, jobOfferId)
  }

  async readMyJobOfferResponses (args: ReadMyResponsesArgs, cursor?: number, take = 10, channelId?: number) {
    const __jobOffer = await import('./jobOffer')
    return await __jobOffer.readMyJobOfferResponses(this, args, cursor, take)
  }

  async updateJobOfferOfMessage (mesgId: number, data: types.JobOfferUpdateInput) {
    const __jobOffer = await import('./jobOffer')
    return await __jobOffer.updateJobOfferOfMessage(this, mesgId, data)
  }

  async updateJobOfferProps (jobOfferId: number, props: types.JobOfferProps) {
    const __jobOffer = await import('./jobOffer')
    return await __jobOffer.updateJobOfferProps(this, jobOfferId, props)
  }

  async updateJobOfferResponseProps (jobOfferResponseId: number, props: types.JobOfferResponseProps) {
    const __jobOffer = await import('./jobOffer')
    return await __jobOffer.updateJobOfferResponseProps(this, jobOfferResponseId, props)
  }

  async updateJobOfferState (jobOfferId: number, newState: types.JobOfferState) {
    const __jobOffer = await import('./jobOffer')
    return await __jobOffer.updateJobOfferState(this, jobOfferId, newState)
  }

  async updateJobOfferResponse (id: number, data: types.JobOfferResponseUpdateInput) {
    const __jobOffer = await import('./jobOffer')
    return await __jobOffer.updateJobOfferResponse(this, id, data)
  }

  async updateJobOfferResponsePrice (id: number, price: number) {
    const __jobOffer = await import('./jobOffer')
    return await __jobOffer.updateJobOfferResponsePrice(this, id, price)
  }

  async updateJobOfferResponseState (id: number, mesgId: number, state: types.JobOfferResponseState) {
    const __jobOffer = await import('./jobOffer')
    return await __jobOffer.updateJobOfferResponseState(this, id, mesgId, state)
  }

  async setJobOfferResponseHired (jrsp: types.JobOfferResponse, chatRoom: types.ChatRoom, mesgId: number, to: types.JobOfferResponseState = 'Hired', partsHired?: string[]) {
    const __jobOffer = await import('./jobOffer')
    return await __jobOffer.setJobOfferResponseHired(this, jrsp, chatRoom, mesgId, to, partsHired)
  }

  async setJobOfferResponseCanceled (id: number, mesgId: number) {
    const __jobOffer = await import('./jobOffer')
    return await __jobOffer.setJobOfferResponseCanceled(this, id, mesgId)
  }

  async setJobOfferResponseWorkFinished (id: number, mesgId: number) {
    const __jobOffer = await import('./jobOffer')
    return await __jobOffer.setJobOfferResponseWorkFinished(this, id, mesgId)
  }

  async checkJobOfferResponseClose (jrsp: types.JobOfferResponse) {
    const __jobOffer = await import('./jobOffer')
    return await __jobOffer.checkJobOfferResponseClose(this, jrsp)
  }

  async removeJobOfferResponseToBeDone (chatRoom: types.ChatRoom, jrsp: types.JobOfferResponse, task: types.ToBeDoneTypes) {
    const __jobOffer = await import('./jobOffer')
    return await __jobOffer.removeJobOfferResponseToBeDone(this, chatRoom, jrsp, task)
  }

  async readMessageSummaries (mesgIds: number[]) {
    const __messageInfo = await import('./messageInfo')
    return await __messageInfo.readMessageSummaries(this, mesgIds)
  }

  async readMessageInfos (mesgIds: number[]) {
    const __messageInfo = await import('./messageInfo')
    return await __messageInfo.readMessageInfos(this, mesgIds)
  }

  async likeMessage (mesgId: number, chatRoomId: number, summary?: types.MessageSummary) {
    const __messageInfo = await import('./messageInfo')
    return await __messageInfo.likeMessage(this, mesgId, chatRoomId, summary)
  }

  async cancelLikeMessage (mesgId: number, summary?: types.MessageSummary) {
    const __messageInfo = await import('./messageInfo')
    return await __messageInfo.cancelLikeMessage(this, mesgId, summary)
  }

  async bookmarkMessage (mesgId: number, chatRoomId: number, summary?: types.MessageSummary) {
    const __messageInfo = await import('./messageInfo')
    return await __messageInfo.bookmarkMessage(this, mesgId, chatRoomId, summary)
  }

  async readBookmarkMessages (cursor?: number, take?: number, channelId?: number) {
    const __messageInfo = await import('./messageInfo')
    return await __messageInfo.readBookmarkMessages(this, cursor, take, channelId)
  }

  async cancelBookmarkMessage (mesgId: number, summary?: types.MessageSummary) {
    const __messageInfo = await import('./messageInfo')
    return await __messageInfo.cancelBookmarkMessage(this, mesgId, summary)
  }

  async subscribeOnMessageSummaryUpdated (chatRoomId: number, cb: MesgSummaryCallback, messageId = 0, userId = 0) {
    const __messageInfo = await import('./messageInfo')
    return await __messageInfo.subscribeOnMessageSummaryUpdated(this, chatRoomId, cb, messageId, userId)
  }

  async readMessageTranslations (mesgId: number, version?: string) {
    const __messageTranslation = await import('./translation')
    return await __messageTranslation.readMessageTranslations(this, mesgId, version)
  }

  async readMessageTranslation (mesgId: number, target: string, version?: string) {
    const __messageTranslation = await import('./translation')
    return await __messageTranslation.readMessageTranslation(this, mesgId, target, version)
  }

  async deleteMessageTranslation (mesgId: number) {
    const __messageTranslation = await import('./translation')
    return await __messageTranslation.deleteMessageTranslation(this, mesgId)
  }

  async createMySearchWord (words: string) {
    const __search = await import('./search')
    return await __search.createMySearchWord(this, words)
  }

  async readMySearchWords () {
    const __search = await import('./search')
    return await __search.readMySearchWords(this)
  }

  async readSearchWordSuggestions () {
    const __search = await import('./search')
    return await __search.readSearchWordSuggestions(this)
  }

  async deleteSearchWord (id: number) {
    const __search = await import('./search')
    return await __search.deleteSearchWord(this, id)
  }

  async deleteAllSearchWords () {
    const __search = await import('./search')
    return await __search.deleteAllSearchWords(this)
  }

  async readSysNotices () {
    const __sysNotice = await import('./sysNotice')
    return await __sysNotice.readSysNotices(this)
  }

  async readSysNotice (id: number) {
    const __sysNotice = await import('./sysNotice')
    return await __sysNotice.readSysNotice(this, id)
  }

  async readUnreadSysNoticeCount () {
    const __sysNotice = await import('./sysNotice')
    return await __sysNotice.readUnreadSysNoticeCount(this)
  }

  async readFaQ () {
    const __faq = await import('./faq')
    return await __faq.readFaQ(this)
  }

  async readDocument (type: string, channelId?: number, lang?: string) {
    const __document = await import('./document')
    return await __document.readDocument(this, type, channelId, lang)
  }

  async createDocument (data: types.DocumentCreateInput) {
    const __document = await import('./document')
    return await __document.createDocument(this, data)
  }

  async readAdvertisement (id: number) {
    const __advertise = await import('./advertise')
    return await __advertise.readAdvertisement(this, id)
  }

  async readAdvertisements (channelId: number) {
    const __advertise = await import('./advertise')
    return await __advertise.readAdvertisements(this, channelId)
  }

  async readUnreadAdsCount () {
    const __advertise = await import('./advertise')
    return await __advertise.readUnreadAdsCount(this)
  }

  async readAllValidAdvertisements () {
    const __advertise = await import('./advertise')
    return await __advertise.readAllValidAdvertisements(this)
  }

  async readAllHomeBannerAdvertisements () {
    const __advertise = await import('./advertise')
    return await __advertise.readAllHomeBannerAdvertisements(this)
  }

  async createUserAdAction (data: types.UserAdActionCreateInput) {
    const __advertise = await import('./advertise')
    return await __advertise.createUserAdAction(this, data)
  }

  async getLatestVersion (version: string, platform: string) {
    const __version = await import('./version')
    return await __version.getLatestVersion(this, version, platform)
  }

  async getUrlMetadata (url: string) {
    const __urlMetadata = await import('./urlMetadata')
    return await __urlMetadata.getUrlMetadata(this, url)
  }

  async paypalCreateOrder (data: PaypalCreateOrderRequest) {
    const __payment = await import('./payment')
    return await __payment.paypalCreateOrder(this, data)
  }

  async paypalCaptureOrder (data: PaypalCreateOrderRequest) {
    const __payment = await import('./payment')
    return await __payment.paypalCaptureOrder(this, data)
  }

  async readSysInfo () {
    const __sysinfo = await import('./sys-info')
    return await __sysinfo.readSysInfo(this)
  }

  async readSecrets () {
    const __sysinfo = await import('./sys-info')
    return await __sysinfo.readSecrets(this)
  }

  async readUserUsage () {
    const __usage = await import('./usage')
    return await __usage.readUserUsage(this)
  }

  async upsertUserUsage (data: types.UserUsageData) {
    const __usage = await import('./usage')
    return await __usage.upsertUserUsage(this, data)
  }

  async updateUserUsage () {
    const __usage = await import('./usage')
    return __usage.updateUserUsage(this)
  }

  async updateUsageVisitChannel (channelId: number) {
    const __usage = await import('./usage')
    return __usage.updateUsageVisitChannel(this, channelId)
  }

  async updateUsageVisitChatRoom (chatRoomId: number) {
    const __usage = await import('./usage')
    return __usage.updateUsageVisitChatRoom(this, chatRoomId)
  }

  async createUserLog (type: string, body: Record<string, unknown> | string) {
    const __userLog = await import('./userLog')
    return await __userLog.createUserLog(this, type, body)
  }

  async createNonUserLog (uuid: string, type: string, body: Record<string, unknown> | string) {
    const __userLog = await import('./userLog')
    return await __userLog.createNonUserLog(this, uuid, type, body)
  }

  async readDefaultProfilePictures () {
    const __sysPicture = await import('./sysPicture')
    return await __sysPicture.readDefaultProfilePictures(this)
  }

  async canEnterMediaRoom (output: types.JobOutput) {
    const __mediaRoom = await import('./mediaRoom')
    return __mediaRoom.canEnterMediaRoom(this, output)
  }

  async joinMediaRoom (output: types.JobOutput) {
    const __mediaRoom = await import('./mediaRoom')
    return await __mediaRoom.joinMediaRoom(this, output)
  }

  async createPageCallRoom (profiles: types.Profile[], urls: string[]) {
    const __pageCall = await import('./page-call')
    return await __pageCall.createPageCallRoom(this, profiles, urls)
  }

  async enterPageCallRoom (roomId: string, profile: types.Profile) {
    const __pageCall = await import('./page-call')
    return await __pageCall.enterPageCallRoom(this, roomId, profile)
  }

  async readPageCallRoom (roomId: string) {
    const __pageCall = await import('./page-call')
    return await __pageCall.readPageCallRoom(this, roomId)
  }

  async closePageCallRoom (roomId: string) {
    const __pageCall = await import('./page-call')
    return await __pageCall.closePageCallRoom(this, roomId)
  }

  async getPageCallAccessToken () {
    const __pageCall = await import('./page-call')
    return __pageCall.getPageCallAccessToken(this)
  }

  async readFollow (channelId: number, userId: number) {
    const __follow = await import('./follow')
    return await __follow.readFollow(this, channelId, userId)
  }

  async readFollowers (channelId: number, cursor?: number, take = 100) {
    const __follow = await import('./follow')
    return await __follow.readFollowers(this, channelId, cursor, take)
  }

  async readFollowerCount (channelId: number) {
    const __follow = await import('./follow')
    return await __follow.readFollowerCount(this, channelId)
  }

  async readUsersFollowedByMe (channelId: number, cursor?: number, take = 100) {
    const __follow = await import('./follow')
    return await __follow.readUsersFollowedByMe(this, channelId, cursor, take)
  }

  async followUser (channelId: number, userId: number) {
    const __follow = await import('./follow')
    return await __follow.followUser(this, channelId, userId)
  }

  async unfollowUser (channelId: number, userId: number) {
    const __follow = await import('./follow')
    return await __follow.unfollowUser(this, channelId, userId)
  }

  async readUserPointLogs (channelId: number, cursor: number) {
    const __reward = await import('./reward')
    return await __reward.readUserPointLogs(this, channelId, cursor)
  }

  async createUserPointWithdrawRequest (channelId: number, point: number) {
    const __reward = await import('./reward')
    return await __reward.createUserPointWithdrawRequest(this, channelId, point)
  }

  async readAccountInfoOfProfile (profile: types.Profile) {
    const __accountInfo = await import('./accountInfo')
    return await __accountInfo.readAccountInfoOfProfile(this, profile)
  }

  async readAccountInfoOfProfileId (profileId: number) {
    const __accountInfo = await import('./accountInfo')
    return await __accountInfo.readAccountInfoOfProfileId(this, profileId)
  }

  async readUserIdentity () {
    const __accountInfo = await import('./accountInfo')
    return await __accountInfo.readUserIdentity(this)
  }

  async readUserIdentityOfUserId (userId: number) {
    const __accountInfo = await import('./accountInfo')
    return await __accountInfo.readUserIdentityOfUserId(this, userId)
  }

  async readPayments (where: types.PaymentWhereInput, take?: number, cursor?: number) {
    const __payment = await import('./payment')
    return await __payment.readPayments(this, where, take, cursor)
  }

  async readPayment (id: number) {
    const __payment = await import('./payment')
    return await __payment.readPayment(this, id)
  }

  async readMySettlementItems (dateFrom: Date, dateTo: Date, take: number, cursor: number | undefined) {
    const __payment = await import('./payment')
    return await __payment.readMySettlementItems(this, dateFrom, dateTo, take, cursor)
  }

  async readSettlements (where: types.SettlementWhereInput) {
    const __payment = await import('./payment')
    return await __payment.readSettlements(this, where)
  }

  async readSettlement (id: number) {
    const __payment = await import('./payment')
    return await __payment.readSettlement(this, id)
  }

  async requestSelfVerification (req: types.VerifySelfRequest) {
    const __vservice = await import('./verification-service')
    return await __vservice.requestSelfVerification(this, req)
  }

  async getSelfVerificationResult (extReqId: string) {
    const __vservice = await import('./verification-service')
    return await __vservice.getSelfVerificationResult(this, extReqId)
  }

  async requestNameIdVerification (req: types.VerifyNameIdRequest) {
    const __vservice = await import('./verification-service')
    return await __vservice.requestNameIdVerification(this, req)
  }

  async requestBankVerification (req: types.VerifyBankRequest) {
    const __vservice = await import('./verification-service')
    return await __vservice.requestBankVerification(this, req)
  }

  async getBankVerificationResult (extReqId: string) {
    const __vservice = await import('./verification-service')
    return await __vservice.getBankVerificationResult(this, extReqId)
  }

  async verifyInAppPurchase (params: InAppPurchaseVerifyParams) {
    const __iap = await import('./in-app-purchase')
    return await __iap.verifyInAppPurchase(this, params)
  }

  async createCommerceProduct (data: types.CommerceProductCreateInput) {
    const __commerce = await import('./commerce')
    return await __commerce.createCommerceProduct(this, data)
  }

  async updateCommerceProduct (id: number, data: types.CommerceProductUpdateInput) {
    const __commerce = await import('./commerce')
    return await __commerce.updateCommerceProduct(this, id, data)
  }

  async readCommerceProducts (where: types.CommerceProductWhereInput, orderBy?: types.CommerceProductOrderByWithRelationInput[], cursor?: number, take?: number): Promise<types.CommerceProduct[] | undefined> {
    const __commerce = await import('./commerce')
    return await __commerce.readCommerceProducts(this, where, orderBy, cursor, take)
  }

  async readCommerceProduct (id: number): Promise<types.CommerceProduct | undefined> {
    const __commerce = await import('./commerce')
    return await __commerce.readCommerceProduct(this, id)
  }

  async createCommerceProductReview (data: types.CommerceProductReviewCreateInput) {
    const __commerce = await import('./commerce')
    return await __commerce.createCommerceProductReview(this, data)
  }

  async readCommerceProductReviews (where: types.CommerceProductReviewWhereInput, orderBy?: types.CommerceProductReviewOrderByWithRelationInput[], cursor?: number, take?: number): Promise<types.CommerceProductReview[] | undefined> {
    const __commerce = await import('./commerce')
    return await __commerce.readCommerceProductReviews(this, where, orderBy, cursor, take)
  }

  async readCommerceProductReview (id: number): Promise<types.CommerceProductReview | undefined> {
    const __commerce = await import('./commerce')
    return await __commerce.readCommerceProductReview(this, id)
  }

  async readMyCommerceProductReview (productId: number): Promise<types.CommerceProductReview | undefined> {
    const __commerce = await import('./commerce')
    return await __commerce.readMyCommerceProductReview(this, productId)
  }

  async createCommerceOrderReview (data: types.CommerceOrderReviewCreateInput) {
    const __commerce = await import('./commerce')
    return await __commerce.createCommerceOrderReview(this, data)
  }

  async readCommerceOrderReviews (where: types.CommerceOrderReviewWhereInput, orderBy?: types.CommerceOrderReviewOrderByWithRelationInput[], cursor?: number, take?: number): Promise<types.CommerceOrderReview[] | undefined> {
    const __commerce = await import('./commerce')
    return await __commerce.readCommerceOrderReviews(this, where, orderBy, cursor, take)
  }

  async readCommerceOrderReview (id: number): Promise<types.CommerceOrderReview | undefined> {
    const __commerce = await import('./commerce')
    return await __commerce.readCommerceOrderReview(this, id)
  }

  async readMyCommerceOrderReview (productId: number, orderId: number): Promise<types.CommerceOrderReview | undefined> {
    const __commerce = await import('./commerce')
    return await __commerce.readMyCommerceOrderReview(this, productId, orderId)
  }

  async createCommerceOrder (data: types.CommerceOrderCreateInput): Promise<types.CommerceOrder | undefined> {
    const __commerce = await import('./commerce')
    const created = await __commerce.createCommerceOrder(this, data)
    return created
  }

  async updateCommerceOrder (id: number, data: types.CommerceOrderUpdateInput): Promise<types.CommerceOrder | undefined> {
    const __commerce = await import('./commerce')
    const created = await __commerce.updateCommerceOrder(this, id, data)
    return created
  }

  async updateCommerceOrderStateAndDetail (id: number, state: types.CommerceOrderFoodState, orderDetail?: Record<string, unknown>): Promise<types.CommerceOrder | undefined> {
    const __commerce = await import('./commerce')
    const updated = await __commerce.updateCommerceOrderStateAndDetail(this, id, state, orderDetail)
    return updated
  }

  async updateManyCommerceOrderState (ids: number[], state: types.CommerceOrderFoodState): Promise<types.CommerceOrder[] | undefined> {
    const __commerce = await import('./commerce')
    const updated = await __commerce.updateManyCommerceOrderState(this, ids, state)
    return updated
  }

  async updateManyCommerceOrderRider (ids: number[], state: types.CommerceOrderFoodState, riderId: number, riderUserId: number): Promise<types.CommerceOrder[] | undefined> {
    const __commerce = await import('./commerce')
    const updated = await __commerce.updateManyCommerceOrderRider(this, ids, state, riderId, riderUserId)
    return updated
  }

  async updateCommerceOrderDetail (id: number, price: number, detail?: Record<string, unknown>): Promise<types.CommerceOrder | undefined> {
    const __commerce = await import('./commerce')
    const updated = await __commerce.updateCommerceOrderDetail(this, id, price, detail)
    return updated
  }

  async readCommerceOrders (where: types.CommerceOrderWhereInput, cursor?: number, take?: number, orderBy?: types.CommerceOrderOrderByWithRelationInput[]): Promise<types.CommerceOrder[] | undefined> {
    const __commerce = await import('./commerce')
    return await __commerce.readCommerceOrders(this, where, cursor, take, orderBy)
  }

  async readCommerceOrder (id: number): Promise<types.CommerceOrder | undefined> {
    const __commerce = await import('./commerce')
    return await __commerce.readCommerceOrder(this, id)
  }

  async deleteCommerceOrder (id: number): Promise<types.CommerceOrder | undefined> {
    const __commerce = await import('./commerce')
    const created = await __commerce.deleteCommerceOrder(this, id)
    return created
  }

  async createPlace (data: types.PlaceCreateInput): Promise<types.Place | undefined> {
    const __commerce = await import('./place')
    return await __commerce.createPlace(this, data)
  }

  async updatePlace (id: number, data: types.PlaceUpdateInput): Promise<types.Place | undefined> {
    const __commerce = await import('./place')
    return await __commerce.updatePlace(this, id, data)
  }

  async readPlaces (where: types.PlaceWhereInput): Promise<types.Place[] | undefined> {
    const __commerce = await import('./place')
    return await __commerce.readPlaces(this, where)
  }

  async deletePlace (id: number): Promise<types.Place | undefined> {
    const __commerce = await import('./place')
    return await __commerce.deletePlace(this, id)
  }

  async clearCache (userChanged?: boolean) {
    await this.thumbnailCache?.reset()
    await this.messageCache?.reset()
    await this.clearSingleCaches(userChanged ?? false)
  }

  private async clearSingleCaches (userChanged: boolean) {
    await this.rootMessageCache?.reset()
    await this.messageNotiCache?.reset()
    await this.failedMessageCache?.reset()
  }

  setDevelMode (devel: boolean) {
    if (devel) {
      debug.enableMemLog(devel)
    }
    if (devel !== this.develMode) {
      this.commitDevelMode(devel)
    }
  }

  setSkipIntro (skipIntro: boolean) {
    this.commitSkipIntro(skipIntro)
  }

  addSysNotifications (notis: types.SysNotification[]) {
    this.commitAddSysNotifications(notis)
  }

  addMesgNotifications (notis: types.MesgNotification[]) {
    this.commitAddMesgNotifications(notis)
  }

  blockChannel (channelId: number) {
    if (!this.blockedChannelIds.includes(channelId)) {
      this.blockedChannelIds.push(channelId)
      this.settings.blockedChannelIds = this.blockedChannelIds.join(',')
    }
  }

  setUserPenaltyNotified (penalty: string) {
    this.userPenaltyNotified = penalty
  }

  isUserPenaltyNotified (penalty: string) {
    return this.userPenaltyNotified === penalty
  }

  // setBookmarkedMessage (mesgId: number, set: boolean) {
  //   this.commitBookmarkedMessage(mesgId, set)
  // }

  // setLikedMessage (mesgId: number, set: boolean) {
  //   this.commitLikedMessage(mesgId, set)
  // }

  //--------------------------------------------------------------------------------
  // mutations
  //--------------------------------------------------------------------------------

  commitSkipIntro (skipIntro: boolean) {
    this.skipIntro = skipIntro
    this.settings.skipIntro = skipIntro
  }

  commitDevelMode (develMode: boolean) {
    this.develMode = develMode
    this.settings.development = develMode
  }

  commitUserId (id: number | undefined) {
    this.userId = id || 0
    this.settings.userId = this.userId
  }

  commitAuthToken (token: string | undefined) {
    this.authToken = token || ''
    this.settings.authToken = this.authToken
    this.apollo.setAuthToken(this.authToken)
    this.watch.publish(this.TOKEN_CHANGED, token)
  }

  commitUser (user: types.User | null) {
    this.user = user
  }

  async commitUserGraph () {
    const tick = Date.now()
    const json = objCache.toJSON()
    // user.myProfiles.jobOffers.client.jobOffers.client...... 으로 jobOffer가 순환 참조 되어 있어서 JSON 처리하면 에러가 발생한다.
    const key = process.env.LOCAL_STORAGE_KEY || 'localkey'
    // const CryptoJS = (await import('crypto-js')).default
    const userGraph = CryptoJS.AES.encrypt(json, key).toString()
    this.settings.userGraph = userGraph
    this.settings.userGraphVersion = USER_GRAPH_VERSION
    // this.settings.userGraph = objCache.toJSON()
    debug.log('---***--- commitUserGraph: tick = ', Date.now() - tick)
  }

  commitClearUserGraph () {
    this.settings.userGraph = ''
  }

  // commitProfiles (profiles: types.Profile[]) {
  //   let changed = false
  //   for (const profile of profiles) {
  //     if (!this.profiles.find((p) => p.id === profile.id)) {
  //       changed = true
  //       break
  //     }
  //   }
  //   if (!changed) {
  //     for (const profile of this.profiles) {
  //       if (!this.profiles.find((p) => p.id === profile.id)) {
  //         changed = true
  //         break
  //       }
  //     }
  //   }
  //   if (changed) {
  //     this.profiles = profiles
  //     this.profileIds = profiles.map((p) => p.id)
  //   } else {
  //     for (let i = 0; i < profiles.length; i++) {
  //       if (this.profiles[i].id !== profiles[i].id) {
  //         throw 'commitProfiles: id are not same'
  //       }
  //       this.profiles[i] = profiles[i]
  //     }
  //   }
  // }

  // commitJoinedChannels (channels: ChannelAndProfiles[]) {
  //   this.joinedChannels = channels
  // }

  // commitExpelledChannelIds (channelIds: number[]) {
  //   this.expelledChannelIds = channelIds
  // }

  commitChannels (channels: types.Channel[]) {
    this.channels = channels
  }

  commitCurrentChannelInfo (info: CurrentChannelInfo) {
    this.currentChannelInfo = info
  }

  // commitBlockedProfiles (blockedProfiles: types.Profile[]) {
  //   this.blockedProfiles = blockedProfiles
  // }

  // commitBlockingProfiles (blockingProfiles: types.Profile[]) {
  //   this.blockingProfiles = blockingProfiles
  // }

  // commitUserBlockings (userBlockings: types.UserBlocking[]) {
  //   this.userBlockings = userBlockings
  // }

  commitResetSysNotifications () {
    this.sysNotifications = []
  }

  commitAddSysNotifications (notis: types.SysNotification[]) {
    const maxCount = 100
    const lastNotiId = this.user?.lastSysNotiId || 0
    const newNotis = [...this.sysNotifications]
    notis.forEach((noti) => {
      if (!newNotis.find((n) => n.noti.id === noti.id)) {
        newNotis.push({
          noti,
          read: noti.id <= lastNotiId,
          text: convertSysNotiToString(noti, i18n.locale),
        })
      }
    })
    this.sysNotifications = newNotis.sort((a, b) => b.noti.id - a.noti.id).slice(0, maxCount)
  }

  commitDeleteSysNotification (notiId: number) {
    const index = this.sysNotifications.findIndex((s) => s.noti.id === notiId)
    if (index >= 0) {
      this.sysNotifications.splice(index, 1)
    }
  }

  commitResetMesgNotifications () {
    this.mesgNotifications = []
  }

  commitAddMesgNotifications (notis: types.MesgNotification[]) {
    const maxCount = 500
    const lastNotiId = this.user?.lastMesgNotiId || 0
    const newNotis = [...this.mesgNotifications]
    notis.forEach((noti) => {
      if (!newNotis.find((n) => n.noti.messageId === noti.messageId)) {
        newNotis.push({
          noti, text: '',
          read: noti.messageId <= lastNotiId,
        })
      }
    })
    this.mesgNotifications = newNotis.sort((a, b) => b.noti.messageId - a.noti.messageId).slice(0, maxCount)
    this.isNotificationReady = true
  }

  commitDeleteMesgNotification (messageId: number) {
    const index = this.mesgNotifications.findIndex((s) => s.noti.messageId === messageId)
    if (index >= 0) {
      this.mesgNotifications.splice(index, 1)
    }
  }

  // commitBookmarkedMessageIds (ids: number[]) {
  //   this.bookmarkedMesgIds = ids
  // }

  // commitBookmarkedMessage (mesgId: number, set: boolean) {
  //   const inx = this.bookmarkedMesgIds.findIndex((id) => id === mesgId)
  //   if (set && inx < 0) {
  //     this.bookmarkedMesgIds.push(mesgId)
  //   } else if (!set && inx >= 0) {
  //     this.bookmarkedMesgIds.splice(inx, 1)
  //   }
  // }

  // commitLikedMessageIds (ids: number[]) {
  //   this.likedMesgIds = ids
  // }

  // commitLikedMessage (mesgId: number, set: boolean) {
  //   const inx = this.likedMesgIds.findIndex((id) => id === mesgId)
  //   if (set && inx < 0) {
  //     this.likedMesgIds.push(mesgId)
  //   } else if (!set && inx >= 0) {
  //     this.likedMesgIds.splice(inx, 1)
  //   }
  // }

  commitUserUsage (usage: types.UserUsageData) {
    this.userUsage = usage
    this.settings.userUsage = JSON.stringify(usage)
  }

  commitAddPrivateChatRoom (chatRoom: types.ChatRoom) {
    const cached = objCache.put(chatRoom) as types.ChatRoom
    this.privateChatRooms.push(cached)
  }

  // commitSysNotices (sysNotices: types.SysNotice[]) {
  //   this.sysNotices = sysNotices
  // }

  // commitAddSysNotice (sysNotice: types.SysNotice) {
  //   this.sysNotices.unshift(sysNotice)
  // }

  // commitAdvertisements (advertisements: types.Advertisement[]) {
  //   this.advertisements = advertisements
  // }

  // commitAddAdvertisement (advertisement: types.Advertisement) {
  //   this.advertisements.unshift(advertisement)
  // }

  commitSysInfo (info: types.SysInfo) {
    this.sysInfo = info
    this.sysInfoExt = JSON.parse(this.sysInfo.ext || '{}') as types.SysInfoExt
    const categories = this.sysInfo.categories
    // const keywords = await this.readCategoryKeywords()
    if (categories) {
      this.categoryKeywords = categories.split(',')
      this.settings.categories = categories
    }
    this.settings.sysInfo = JSON.stringify(info)
  }

  commitSecrets (secrets: types.Secret[]) {
    this.secrets = secrets
  }

  // commitMyJobOffers (offers: types.JobOffer[]) {
  //   const myOffers: types.JobOffer[] = []
  //   offers.forEach((offer) => {
  //     const profile = this.profiles.find((p) => p.id === offer.clientId)
  //     if (profile) {
  //       if (profile.jobOffers?.find((o) => o.id === offer.id)) {
  //         myOffers.push(objCache.update(offer) as types.JobOffer)
  //       } else {
  //         if (profile.jobOffers?.length) {
  //           profile.jobOffers.push(offer)
  //         } else {
  //           profile.jobOffers = [offer]
  //         }
  //         myOffers.push(objCache.addLink(profile, offer) as types.JobOffer)
  //       }
  //     }
  //   })
  //   this.myJobOffers = myOffers
  // }

  // commitMyJobOfferResponses (jrsps: types.JobOfferResponse[]) {
  //   const myRsps: types.JobOfferResponse[] = []
  //   jrsps.forEach((jrsp) => {
  //     const profile = this.profiles.find((p) => p.id === jrsp.providerId)
  //     if (profile) {
  //       if (profile.jobOfferResponses?.find((o) => o.id === jrsp.id)) {
  //         myRsps.push(objCache.update(jrsp) as types.JobOfferResponse)
  //       } else {
  //         if (profile.jobOfferResponses?.length) {
  //           profile.jobOfferResponses.push(jrsp)
  //         } else {
  //           profile.jobOfferResponses = [jrsp]
  //         }
  //         myRsps.push(objCache.addLink(profile, jrsp) as types.JobOfferResponse)
  //       }
  //     }
  //   })
  //   this.myJobOfferResponses = myRsps
  // }

  // commitUserBlockings (blockings: types.UserBlocking[]) {
  //   this.userBlockings = blockings
  // }

  commitAddUserBlocking (blocking: types.UserBlocking) {
    if (this.user) {
      if (this.userId === blocking.blockerId) {
        this.user.blockedUsers.push(blocking)
        this.watch.publish(this.USER_BLOCKING_CHANGED, { added: blocking })
      } else {
        this.user.blockingUsers.push(blocking)
      }
    }
  }

  commitDeleteUserBlocking (blocking: types.UserBlocking) {
    if (this.user) {
      let blockings: types.UserBlocking[]
      if (this.userId === blocking.blockerId) {
        blockings = this.user.blockedUsers
      } else {
        blockings = this.user.blockingUsers
      }
      const inx = blockings.findIndex((i) => i.id === blocking.id)
      if (inx >= 0) {
        blockings.splice(inx, 1)
        if (this.userId === blocking.blockerId) {
          this.watch.publish(this.USER_BLOCKING_CHANGED, { deleted: blocking })
        }
      }
    }
  }

  // commitChannelChatRoomMenus (menus: types.ChannelChatRoomMenu[]) {
  //   this.chatRoomMenus = menus
  // }

  // commitAddChannelChatRoomMenus (menus: types.ChannelChatRoomMenu[]) {
  //   menus.forEach((menu) => {
  //     if (!this.chatRoomMenus.find((m) => m.id === menu.id)) {
  //       this.chatRoomMenus.push(menu)
  //     }
  //   })
  // }

  commitAddDontShowAgain (dont: types.DontShowAgain) {
    this.dontShowAgains.push(dont)
  }

  commitUnreadSysNoticeCount (count: number) {
    this.unreadSysNoticeCount = count
  }

  commitUnreadAdsCount (count: number) {
    this.unreadAdsCount = count
  }

  commitMyChannelGraphs (channels: types.Channel[]) {
    channels.forEach((channel) => {
      const p_c = this.user?.profileChannels.find((p_c) => p_c.channelId === channel.id)
      if (p_c) {
        const cached = objCache.addLink(p_c, channel) as types.Channel
        Vue.set(p_c, 'channel', cached)
      } else {
        console.error('commitMyChannelGraphs: failed to find p_c of channel ', channel, this.user?.profileChannels)
      }
    })
  }

  commitMyChannelCounts (channels: types.Channel[]) {
    channels.forEach((ch) => {
      const cached = objCache.get(ch) as types.Channel
      if (cached) {
        Vue.set(cached, 'profileCount', ch.profileCount)
      }
    })
  }
}
